import { Fragment, useContext, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  ListItemText,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  InputAdornment,
  FormGroup,
  Collapse,
  Autocomplete,
  Chip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import LoupeIcon from '@mui/icons-material/Loupe';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import GavelIcon from '@mui/icons-material/Gavel';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import ImportISLP from './ImportISLP';
import RepriseDialog from './RepriseDialog';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ThemeContext } from '../Context';
import { LeftPanelContext } from '../Context';
import { ReportElementsContext } from '../Context';
import axios from 'axios';
import { logAction, formatReportNumber, LOG_ACTIONS, contentLengthLog } from '../../utils/logClient';
import { getEncodagePalette } from '../../theme/encodagePalette';

// Sections de l'encodage, dans leur ordre d'affichage
const SECTIONS = [
  { key: 'intervenants', label: 'Intervenants', singular: 'Intervenant', icon: BadgeIcon, addLabel: 'Ajouter un intervenant' },
  { key: 'faits', label: 'Faits', singular: 'Fait', icon: GavelIcon, addLabel: 'Ajouter un fait' },
  { key: 'personnesPhysiques', label: 'Personnes physiques', singular: 'Personne physique', icon: PersonIcon, addLabel: 'Ajouter une personne physique' },
  { key: 'personnesMorales', label: 'Personnes morales', singular: 'Personne morale', icon: BusinessIcon, addLabel: 'Ajouter une personne morale' },
  { key: 'vehicules', label: 'Véhicules', singular: 'Véhicule', icon: DirectionsCarIcon, addLabel: 'Ajouter un véhicule' },
  { key: 'objets', label: 'Objets', singular: 'Objet', icon: Inventory2Icon, addLabel: 'Ajouter un objet' }
];

const COLLAPSIBLE_KEYS = ['general', ...SECTIONS.map((section) => section.key)];

const MONO_FONT = 'Consolas, "Roboto Mono", "Courier New", monospace';

const norm = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const compact = (value) => norm(value).replace(/[^a-z0-9]/g, '');

const truncate = (text, max) => (text.length > max ? `${text.slice(0, max).trim()}…` : text);
// Données de l'encodage partagées en direct avec les formulaires de rubrique (ReportElementsContext)
const SHARED_ELEMENT_KEYS = [
  'number',
  'year',
  'unit',
  'reportType',
  'indice',
  'intervenants',
  'faits',
  'personnesPhysiques',
  'personnesMorales',
  'objets',
  'vehicules'
];

const formatFaitHeading = (fait) => {
  if (!fait) return '';
  const introductions = [];
  if (fait.isQualified) introductions.push('Fait qualifié de');
  if (fait.isTentative) introductions.push('Tentative de');
  const noyau = fait.fait || '';
  if (!introductions.length) return noyau;

  const texte = introductions.map((introduction, index) => {
    const suivants = introductions.length - index;
    const libelle = index === 0
      ? introduction
      : `${introduction.charAt(0).toLowerCase()}${introduction.slice(1)}`;
    return suivants > 1 ? libelle : `${libelle} :`;
  }).join(' ');

  return `${texte} ${noyau}`.trim();
};

function Elements() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { highlightTarget } = useContext(LeftPanelContext);
  const { setReportElements } = useContext(ReportElementsContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';
  const c = getEncodagePalette(isDark);

  // Surbrillance temporaire des boutons Ajouter/Modifier, déclenchée par le guide de démarrage
  // ou par un formulaire de rubrique : true = tous les boutons, sinon clé de la section ciblée
  const [guideHighlight, setGuideHighlight] = useState(false);
  const sectionRefs = useRef({});

  // À spreader dans les boutons d'en-tête de section pendant la surbrillance
  const highlightSx = (section) => (guideHighlight === true || (section && guideHighlight === section)) ? {
    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(25, 118, 210, 0.08)',
    animation: 'guidePulse 1s ease-in-out infinite',
    '@keyframes guidePulse': {
      '0%, 100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
      '50%': {
        boxShadow: isDark
          ? '0 0 0 5px rgba(79, 195, 247, 0.35)'
          : '0 0 0 5px rgba(25, 118, 210, 0.3)'
      }
    }
  } : {};
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // États pour la boîte de dialogue de modification d'unité
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [unit, setUnit] = useState('');
  const [unitError, setUnitError] = useState('');

  // États pour la boîte de dialogue de rédaction
  const [openRedactionDialog, setOpenRedactionDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [redactionErrors, setRedactionErrors] = useState({});

  // États pour les intervenants
  const [openIntervenantDialog, setOpenIntervenantDialog] = useState(false);
  const [intervenantMode, setIntervenantMode] = useState('add');
  const [intervenantItemId, setIntervenantItemId] = useState(null);
  const [isExternalMember, setIsExternalMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isSignataire, setIsSignataire] = useState(false);
  const [isOPJ, setIsOPJ] = useState(false);
  const [isOPA, setIsOPA] = useState(false);
  const [rank, setRank] = useState('');
  const [lastname, setLastname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [intervenantErrors, setIntervenantErrors] = useState({});

  // États pour les personnes concernées
  const [openPersonneDialog, setOpenPersonneDialog] = useState(false);
  const [personneMode, setPersonneMode] = useState('add');
  const [personneItemId, setPersonneItemId] = useState(null);
  const [personneQualite, setPersonneQualite] = useState('');
  const [additionalQualities, setAdditionalQualities] = useState([]);
  const [isUnknown, setIsUnknown] = useState(false);
  const [personneLastname, setPersonneLastname] = useState('');
  const [personneFirstname, setPersonneFirstname] = useState('');
  const [otherFirstnames, setOtherFirstnames] = useState('');
  const [gender, setGender] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [civilStatus, setCivilStatus] = useState('');
  const [nationality, setNationality] = useState('');
  const [profession, setProfession] = useState('');
  const [residence, setResidence] = useState('');
  const [nationalNumber, setNationalNumber] = useState('');
  const [raavisNumber, setRaavisNumber] = useState('');
  const [photographies, setPhotographies] = useState('');
  const [empreintes, setEmpreintes] = useState('');
  const [noticeIndividuelle, setNoticeIndividuelle] = useState('');
  const [personneErrors, setPersonneErrors] = useState({});

  // États pour les objets
  const [openObjetDialog, setOpenObjetDialog] = useState(false);
  const [objetMode, setObjetMode] = useState('add');
  const [objetItemId, setObjetItemId] = useState(null);
  const [description, setDescription] = useState('');
  const [sinNumber, setSinNumber] = useState('');
  const [objetErrors, setObjetErrors] = useState({});

  // États pour les véhicules
  const [openVehiculeDialog, setOpenVehiculeDialog] = useState(false);
  const [vehiculeMode, setVehiculeMode] = useState('add');
  const [vehiculeItemId, setVehiculeItemId] = useState(null);
  const [brand, setBrand] = useState('');
  const [vehiculeCategory, setVehiculeCategory] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [chassisNumber, setChassisNumber] = useState('');
  const [vehiculeNationality, setVehiculeNationality] = useState('');
  const [vehiculeColor, setVehiculeColor] = useState('');
  const [insurance, setInsurance] = useState('');
  const [technicalControl, setTechnicalControl] = useState('');
  const [vehiculeErrors, setVehiculeErrors] = useState({});

  // États pour les personnes morales
  const [openPersonneMoraleDialog, setOpenPersonneMoraleDialog] = useState(false);
  const [personneMoraleMode, setPersonneMoraleMode] = useState('add');
  const [personneMoraleItemId, setPersonneMoraleItemId] = useState(null);
  const [personneMoraleQualite, setPersonneMoraleQualite] = useState('');
  const [denomination, setDenomination] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [headquarters, setHeadquarters] = useState('');
  const [personneMoraleErrors, setPersonneMoraleErrors] = useState({});

  // Liste des qualités disponibles pour les personnes morales (avec possibilité d'ajout dynamique)
  const [personneMoraleQualitesList, setPersonneMoraleQualitesList] = useState([
    'Préjudicié',
    'Autre'
  ]);

  // États pour les faits
  const [openFaitDialog, setOpenFaitDialog] = useState(false);
  const [faitMode, setFaitMode] = useState('add');
  const [faitItemId, setFaitItemId] = useState(null);
  const [isUndetermined, setIsUndetermined] = useState(false);
  const [isQualified, setIsQualified] = useState(false);
  const [isTentative, setIsTentative] = useState(false);
  const [fait, setFait] = useState('');
  const [article, setArticle] = useState('');
  const [timeType, setTimeType] = useState('fixed');
  const [fixedDateTime, setFixedDateTime] = useState('');
  const [rangeLowDateTime, setRangeLowDateTime] = useState('');
  const [rangeHighDateTime, setRangeHighDateTime] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [destination, setDestination] = useState('');
  const [faitErrors, setFaitErrors] = useState({});
  
  // État pour la confirmation de suppression
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  
  // État pour la notification de copie
  const [copyNotification, setCopyNotification] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [openImportISLP, setOpenImportISLP] = useState(false);
  
  // Référence pour suivre les dernières actions afin d'éviter les doublons
  const lastActionRef = useRef({
    type: null,
    id: null,
    timestamp: null
  });

  // Liste des grades disponibles
  const gradesList = [
    'Aspirant Agent de Police',
    'Agent de Police',
    'Premier Agent de Police',
    'Aspirant Agent de sécurisation de Police',
    'Agent de sécurisation de Police',
    'Premier Agent de sécurisation de Police',
    'Aspirant Assistant de Sécurisation de Police',
    'Assistant de Sécurisation de Police',
    'Premier Assistant de Sécurisation de Police',
    'Coordonnateur de Sécurisation de Police',
    'Premier Coordonnateur de Sécurisation de Police',
    'Aspirant Inspecteur de Police',
    'Inspecteur de Police',
    'Premier Inspecteur de Police',
    'Aspirant Inspecteur Principal de Police',
    'Inspecteur Principal de Police',
    'Premier Inspecteur Principal de Police',
    'Aspirant Commissaire de Police',
    'Commissaire de Police',
    'Premier Commissaire de Police',
    'Commissaire Divisionnaire de Police',
    'Premier Commissaire Divisionnaire de Police'
  ];

  // État pour la reprise depuis un autre PV
  const [openRepriseDialog, setOpenRepriseDialog] = useState(false);

  // États pour le dialog de modification du type de PV
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [reportType, setReportType] = useState('subsequent');
  const [indice, setIndice] = useState('');
  const [indiceError, setIndiceError] = useState('');

  // États pour gérer l'ouverture/fermeture des sections
  const [sectionExpanded, setSectionExpanded] = useState({
    general: true,
    intervenants: true,
    faits: true,
    personnesPhysiques: true,
    personnesMorales: true,
    vehicules: true,
    objets: true
  });

  // Filtre des éléments encodés
  const [filterText, setFilterText] = useState('');
  const lastDeletePreviewRef = useRef(null);

  // Fonction pour toggler l'ouverture/fermeture d'une section
  const toggleSection = (section) => {
    setSectionExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const setAllSectionsExpanded = (expanded) => {
    setSectionExpanded(COLLAPSIBLE_KEYS.reduce((acc, key) => ({ ...acc, [key]: expanded }), {}));
  };

  // Guidage vers l'encodage : ouvre et fait défiler jusqu'à la section ciblée si elle est précisée
  useEffect(() => {
    if (!highlightTarget || highlightTarget.view !== 'elements') return;
    const { section } = highlightTarget;
    setGuideHighlight(section || true);
    setFilterText('');
    let frame = null;
    if (!section) {
      setSectionExpanded(prev => ({ ...prev, general: true }));
    } else {
      setSectionExpanded(prev => ({ ...prev, [section]: true }));
      // Attendre que la vue Encodage soit affichée avant de faire défiler
      frame = requestAnimationFrame(() => {
        sectionRefs.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
    const timer = setTimeout(() => setGuideHighlight(false), 4000);
    return () => {
      clearTimeout(timer);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [highlightTarget]);

  const handleOpenImportISLP = () => {
    setOpenImportISLP(true);
  };

  const handleCloseImportISLP = () => {
    setOpenImportISLP(false);
  };

  // Fonction pour convertir les acronymes de grade en forme complète
  const convertRankAcronym = (acronym) => {
    if (!acronym) return 'Membre';
    
    const rankMapping = {
      'AAGP': 'Aspirant Agent de Police',
      'AGP': 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      'ADAGP': 'Aspirant Agent de sécurisation de Police',
      'DAGP': 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      'ABASP': 'Aspirant Assistant de Sécurisation de Police',
      'BASP': 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      'BCSP': 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      'AINP': 'Aspirant Inspecteur de Police',
      'INP': 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      'AINPP': 'Aspirant Inspecteur Principal de Police',
      'INPP': 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      'ACP': 'Aspirant Commissaire de Police',
      'CP': 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      'CDP': 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    
    return rankMapping[acronym] || acronym;
  };

  // Liste des qualités de personnes concernées (avec possibilité d'ajout dynamique)
  const [personneQualitesList, setPersonneQualitesList] = useState([
    'Suspect',
    'Victime',
    'Témoin',
    'Source',
    'Propriétaire',
    'Contrevenant',
    'Conducteur',
    'Passager',
    'Civilement responsable',
    'Autre'
  ]);

  // Charger les données du rapport
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        if (!report) {
          setLoading(false);
          return;
        }

        const token = sessionStorage.getItem('authToken');
        
        const response = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setReportData(response.data);
        setUnit(response.data.unit || '');
        setReportType(response.data.reportType || 'subsequent');
        setIndice(response.data.indice || '');
        
        // Initialiser les données de rédaction
        setStartDate(response.data.StartDate || '');
        setEndDate(response.data.EndDate || '');
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement du PV:', error);
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Charger la liste des utilisateurs pour les intervenants
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      }
    };

    fetchUsers();
  }, []);


  // Écouter les mises à jour via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdated = (updatedReport) => {
      if (updatedReport.number === report.number && 
          updatedReport.year === report.year) {
        
        // Vérifier si cette mise à jour correspond à une action récente (moins de 2 secondes)
        const now = Date.now();
        const isRecentAction = lastActionRef.current.timestamp && 
          (now - lastActionRef.current.timestamp < 2000) && 
          lastActionRef.current.type === Object.keys(updatedReport).find(key => 
            ['unit', 'intervenants', 'faits', 'personnesPhysiques', 'personnesMorales', 'objets', 'vehicules', 'redaction'].includes(key)
          );
          
        if (!isRecentAction) {
          setReportData(prevData => ({
            ...prevData,
            ...updatedReport,
          }));
          
          if (updatedReport.unit) {
            setUnit(updatedReport.unit);
          }
          
          if (updatedReport.reportType !== undefined) {
            setReportType(updatedReport.reportType || 'subsequent');
          }
          
          if (updatedReport.indice !== undefined) {
            setIndice(updatedReport.indice || '');
          }
          
          if (updatedReport.StartDate !== undefined || updatedReport.EndDate !== undefined) {
            setStartDate(updatedReport.StartDate || '');
            setEndDate(updatedReport.EndDate || '');
          }
        }
      }
    };

    socket.on('reportUpdated', handleReportUpdated);

    return () => {
      socket.off('reportUpdated', handleReportUpdated);
    };
  }, [socket, report]);

  // Partage en direct de l'encodage avec les formulaires de rubrique ouverts.
  // Seules les listes d'éléments sont exposées, et uniquement quand elles changent,
  // pour ne pas recalculer les formulaires à chaque modification de rubrique reçue par WebSocket.
  const sharedElementsRef = useRef(null);

  useEffect(() => {
    if (!reportData) return;
    const previous = sharedElementsRef.current;
    if (previous && SHARED_ELEMENT_KEYS.every(key => previous[key] === reportData[key])) return;
    const next = SHARED_ELEMENT_KEYS.reduce((acc, key) => ({ ...acc, [key]: reportData[key] }), {});
    sharedElementsRef.current = next;
    setReportElements(next);
  }, [reportData, setReportElements]);

  useEffect(() => () => setReportElements(null), [setReportElements]);

  // Gestion de l'unité
  const handleOpenUnitDialog = () => {
    setUnit(reportData?.unit || '');
    setOpenUnitDialog(true);
    setUnitError('');
  };

  const handleCloseUnitDialog = () => {
    setOpenUnitDialog(false);
  };

  const validateUnit = (value) => {
    if (!value || value.trim() === '') {
      setUnitError('L\'unité est obligatoire');
      return false;
    }
    setUnitError('');
    return true;
  };

  const handleSaveUnit = async () => {
    if (!validateUnit(unit)) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'unit',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        unit
      }));
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          unit: unit,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/unit`,
        { unit: unit },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification du service
      logAction(LOG_ACTIONS.ELEMENTS_UNIT_UPDATE, formatReportNumber(report.number, report.year), {
        ancienService: reportData?.unit || null,
        nouveauService: unit
      });
      
      handleCloseUnitDialog();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'unité:', error);
      handleCloseUnitDialog();
    }
  };

  // Fonction pour convertir une date ISO en format datetime-local
  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return '';
    }
  };

  // Fonction pour convertir une date ISO en format date (YYYY-MM-DD)
  const formatDateLocal = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return '';
    }
  };

  // Gestion de la rédaction
  const handleOpenRedactionDialog = () => {
    setOpenRedactionDialog(true);
    setStartDate(formatDateTimeLocal(reportData?.StartDate) || '');
    setEndDate(formatDateLocal(reportData?.EndDate) || '');
    setRedactionErrors({});
  };

  const handleCloseRedactionDialog = () => {
    setOpenRedactionDialog(false);
  };

  const validateRedactionForm = () => {
    const errors = {};
    setRedactionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveRedaction = async () => {
    if (!validateRedactionForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'redaction',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        StartDate: startDate,
        EndDate: endDate
      }));
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          StartDate: startDate,
          EndDate: endDate,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/redaction`,
        { 
          StartDate: startDate,
          EndDate: endDate
        },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification des dates de rédaction
      logAction(LOG_ACTIONS.ELEMENTS_REDACTION_UPDATE, formatReportNumber(report.number, report.year), {
        ancienneStartDate: reportData?.StartDate || null,
        ancienneEndDate: reportData?.EndDate || null,
        nouvelleStartDate: startDate,
        nouvelleEndDate: endDate
      });
      
      handleCloseRedactionDialog();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des informations de rédaction:', error);
      handleCloseRedactionDialog();
    }
  };

  // Fonction pour mettre à jour seulement la date de clôture à aujourd'hui
  const handleSetEndDateToday = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      // Utiliser l'heure locale au lieu de UTC pour obtenir la date du jour correcte
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`;

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'redaction',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        EndDate: today
      }));
      
      setEndDate(today);
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          StartDate: reportData?.StartDate,
          EndDate: today,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/redaction`,
        { 
          StartDate: reportData?.StartDate,
          EndDate: today
        },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification de la date de clôture
      logAction(LOG_ACTIONS.ELEMENTS_REDACTION_UPDATE, formatReportNumber(report.number, report.year), {
        ancienneEndDate: reportData?.EndDate || null,
        nouvelleEndDate: today,
        action: 'date_cloture_aujourdhui'
      });
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la date de clôture:', error);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteDialog = (type, itemId) => {
    setDeleteType(type);
    setItemToDelete(itemId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  const handleDeleteItem = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId || !itemToDelete) {
        console.error('ID du rapport ou de l\'élément non disponible');
        handleCloseDeleteDialog();
        return;
      }

      // Récupérer les données de l'élément avant suppression pour le log
      const deletedItemData = reportData?.[deleteType]?.find(item => item._id === itemToDelete);

      // Vérifier si l'ID est temporaire
      if (itemToDelete.startsWith && itemToDelete.startsWith('temp-')) {
        // Pour les IDs temporaires, on fait juste une suppression locale
        setReportData(prevData => {
          const updatedItems = prevData[deleteType].filter(item => item._id !== itemToDelete);
          return {
            ...prevData,
            [deleteType]: updatedItems
          };
        });
        
        handleCloseDeleteDialog();
        return;
      }

      const endpoints = {
        intervenants: '/intervenants',
        faits: '/faits',
        personnesPhysiques: '/personnes-physiques',
        personnesMorales: '/personnes-morales',
        objets: '/objets',
        vehicules: '/vehicules'
      };

      const endpoint = endpoints[deleteType];
      const url = `/api/report/${reportId}${endpoint}/${itemToDelete}`;
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: deleteType,
        id: itemToDelete,
        timestamp: Date.now()
      };
      
      // Suppression locale immédiate
      setReportData(prevData => {
        const updatedItems = prevData[deleteType].filter(item => item._id !== itemToDelete);
        return {
          ...prevData,
          [deleteType]: updatedItems
        };
      });
      
      // Suppression via API
      await axios.delete(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      // Log de la suppression avec les données de l'élément
      const deleteLogActions = {
        intervenants: LOG_ACTIONS.ELEMENTS_INTERVENANT_DELETE,
        faits: LOG_ACTIONS.ELEMENTS_FAIT_DELETE,
        personnesPhysiques: LOG_ACTIONS.ELEMENTS_PERSONNE_DELETE,
        personnesMorales: LOG_ACTIONS.ELEMENTS_MORALE_DELETE,
        objets: LOG_ACTIONS.ELEMENTS_OBJET_DELETE,
        vehicules: LOG_ACTIONS.ELEMENTS_VEHICULE_DELETE
      };
      
      logAction(deleteLogActions[deleteType], formatReportNumber(report.number, report.year), {
        type: deleteType,
        elementId: itemToDelete,
        elementData: deletedItemData || null
      });
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'élément:', error);
      handleCloseDeleteDialog();
    }
  };

  const handleCloseNotification = () => {
    setCopyNotification(false);
  };

  const showCopyNotification = (text) => {
    setCopiedText(text);
    setCopyNotification(true);
  };

  const handleCopyToClipboard = (text, html = null) => {
    const copyWithExecCommand = (content, isHtml = false) => {
      if (typeof document === 'undefined' || typeof window === 'undefined') {
        return false;
      }
      
      const element = isHtml ? document.createElement('div') : document.createElement('textarea');
      element.style.position = 'fixed';
      element.style.left = '-9999px';
      element.style.top = '0';
      element.style.pointerEvents = 'none';
      element.style.opacity = '0';
      element.setAttribute('aria-hidden', 'true');
      
      if (isHtml) {
        element.setAttribute('contenteditable', 'true');
        element.innerHTML = content;
      } else {
        element.value = content;
      }
      
      document.body.appendChild(element);
      
      let selection = null;
      if (isHtml) {
        const range = document.createRange();
        range.selectNodeContents(element);
        selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        element.focus();
        element.select();
      }
      
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (error) {
        console.error('Erreur lors de la copie via execCommand:', error);
      }
      
      if (selection) {
        selection.removeAllRanges();
      }
      document.body.removeChild(element);
      return success;
    };
    
    const copyTextFallback = () => {
      const success = copyWithExecCommand(text, false);
      if (success) {
        showCopyNotification(text);
      }
      return success;
    };
    
    if (html) {
      const htmlCopied = copyWithExecCommand(html, true);
      if (htmlCopied) {
        showCopyNotification(text);
        return;
      }
    }
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          showCopyNotification(text);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          copyTextFallback();
        });
    } else {
      copyTextFallback();
    }
  };

  // Fonctions pour gérer les intervenants
  const resetIntervenantForm = () => {
    setIsExternalMember(false);
    setSelectedUserId('');
    setIsSignataire(false);
    setIsOPJ(false);
    setIsOPA(false);
    setRank('');
    setLastname('');
    setFirstname('');
    setIntervenantErrors({});
  };

  const syncFlagsFromUser = (userId, fallback = { opj: false, opa: false }) => {
    if (!userId) {
      setIsOPJ(!!fallback.opj);
      setIsOPA(!!fallback.opa);
      return false;
    }
    const selectedUser = users.find(u => u._id === userId);
    if (selectedUser) {
      setIsOPJ(!!selectedUser.opj);
      setIsOPA(!!selectedUser.opa);
      return true;
    }
    setIsOPJ(!!fallback.opj);
    setIsOPA(!!fallback.opa);
    return false;
  };

  const handleOpenIntervenantDialog = (mode = 'add', itemId = null) => {
    setIntervenantMode(mode);
    setIntervenantItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const intervenant = reportData.intervenants.find(item => item._id === itemId);
      if (intervenant) {
        const isExternal = intervenant.isExternal;
        setIsExternalMember(isExternal);
        setIsSignataire(intervenant.sign || false);
        setRank(intervenant.rank);
        setLastname(intervenant.lastname);
        setFirstname(intervenant.firstname);
        setSelectedUserId(intervenant.userId || '');
        if (isExternal) {
          setIsOPJ(!!intervenant.isOPJ);
          setIsOPA(!!intervenant.isOPA);
        } else if (!syncFlagsFromUser(intervenant.userId, { opj: intervenant.isOPJ, opa: intervenant.isOPA })) {
          setIsOPJ(!!intervenant.isOPJ);
          setIsOPA(!!intervenant.isOPA);
        }
      }
    } else {
      resetIntervenantForm();
    }
    
    setOpenIntervenantDialog(true);
  };

  const handleCloseIntervenantDialog = () => {
    setOpenIntervenantDialog(false);
    resetIntervenantForm();
  };

  const validateIntervenantForm = () => {
    const errors = {};
    
    if (isExternalMember) {
      if (!rank) errors.rank = 'Le grade est obligatoire';
      if (!lastname) errors.lastname = 'Le nom est obligatoire';
      if (!firstname) errors.firstname = 'Le prénom est obligatoire';
    } else {
      if (!selectedUserId) errors.userId = 'Veuillez sélectionner un utilisateur';
    }
    
    setIntervenantErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveIntervenant = async () => {
    if (!validateIntervenantForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      let intervenantData;
      
      if (isExternalMember) {
        intervenantData = {
          isExternal: true,
          sign: isSignataire,
          isOPJ: !!isOPJ,
          isOPA: !!isOPA,
          rank,
          lastname,
          firstname
        };
      } else {
        const selectedUser = users.find(u => u._id === selectedUserId);
        if (!selectedUser) {
          setIntervenantErrors({ userId: 'Utilisateur non trouvé' });
          return;
        }

        intervenantData = {
          isExternal: false,
          sign: isSignataire,
          isOPJ: !!selectedUser.opj,
          isOPA: !!selectedUser.opa,
          rank: convertRankAcronym(selectedUser.rank) || 'Membre',
          lastname: selectedUser.lastname,
          firstname: selectedUser.firstname,
          userId: selectedUser._id
        };
      }

      const endpoint = `/intervenants${intervenantMode === 'edit' ? `/${intervenantItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = intervenantMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'intervenants',
        id: intervenantItemId,
        timestamp: Date.now()
      };
      
      if (intervenantMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: intervenantData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.intervenants];
          const index = updatedItems.findIndex(item => item._id === intervenantItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...intervenantData
            };
          }
          return {
            ...prevData,
            intervenants: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: intervenantData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification d'intervenant
      const logActionType = intervenantMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_INTERVENANT_ADD 
        : LOG_ACTIONS.ELEMENTS_INTERVENANT_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: intervenantMode,
        intervenant: intervenantData
      });
      
      handleCloseIntervenantDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${intervenantMode === 'add' ? 'ajout' : 'édition'} de l'intervenant:`, error);
    }
  };

  const handleCopyIntervenant = (intervenant) => {
    const rankLabel = convertRankAcronym(intervenant.rank);
    const namePart = `${intervenant.lastname?.toUpperCase() || ''}${intervenant.firstname ? ` ${intervenant.firstname}` : ''}`.trim();
    const textToCopy = `${rankLabel ? `${rankLabel} ` : ''}${namePart}`.trim();
    const htmlContent = `${rankLabel ? `${rankLabel} ` : ''}<strong>${namePart}</strong>`;

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'intervenant',
        ...contentLengthLog(textToCopy)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les personnes concernées
  const resetPersonneForm = () => {
    setPersonneQualite('');
    setAdditionalQualities([]);
    setIsUnknown(false);
    setPersonneLastname('');
    setPersonneFirstname('');
    setOtherFirstnames('');
    setGender('');
    setBirthPlace('');
    setBirthdate('');
    setCivilStatus('');
    setNationality('');
    setProfession('');
    setResidence('');
    setNationalNumber('');
    setRaavisNumber('');
    setPhotographies('');
    setEmpreintes('');
    setNoticeIndividuelle('');
    setPersonneErrors({});
  };

  const handleOpenPersonneDialog = (mode = 'add', itemId = null) => {
    setPersonneMode(mode);
    setPersonneItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const personne = reportData.personnesPhysiques.find(item => item._id === itemId);
      if (personne) {
        setPersonneQualite(personne.qualite);
        setAdditionalQualities(personne.additionalQualities || []);
        setIsUnknown(personne.isUnknown);
        setPersonneLastname(personne.lastname || '');
        setPersonneFirstname(personne.firstname || '');
        setOtherFirstnames(personne.otherFirstnames || '');
        setGender(personne.gender || '');
        setBirthPlace(personne.birthPlace || '');
        setBirthdate(personne.birthdate || '');
        setCivilStatus(personne.civilStatus || '');
        setNationality(personne.nationality || '');
        setProfession(personne.profession || '');
        setResidence(personne.residence || '');
        setNationalNumber(personne.nationalNumber || '');
        setRaavisNumber((personne.raavisNumber || '').toUpperCase());
        setPhotographies(personne.photographies || '');
        setEmpreintes(personne.empreintes || '');
        setNoticeIndividuelle(personne.noticeIndividuelle || '');
      }
    } else {
      resetPersonneForm();
    }
    
    setOpenPersonneDialog(true);
  };

  const handleClosePersonneDialog = () => {
    setOpenPersonneDialog(false);
    resetPersonneForm();
  };

  const validatePersonneForm = () => {
    const errors = {};
    
    if (!personneQualite) {
      errors.qualite = 'La qualité est obligatoire';
    }
    
    if (!isUnknown && !personneLastname) {
      errors.lastname = 'Le nom est obligatoire pour une personne identifiée';
    }
    
    setPersonneErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePersonne = async () => {
    if (!validatePersonneForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const normalizedRaavisNumber = (raavisNumber || '').toUpperCase();

      const personneData = {
        qualite: personneQualite,
        additionalQualities: additionalQualities,
        isUnknown,
        lastname: isUnknown ? null : personneLastname,
        firstname: isUnknown ? null : personneFirstname,
        otherFirstnames: isUnknown ? null : otherFirstnames,
        gender: isUnknown ? null : gender,
        birthPlace: isUnknown ? null : birthPlace,
        birthdate: isUnknown ? null : birthdate,
        civilStatus: isUnknown ? null : civilStatus,
        nationality: isUnknown ? null : nationality,
        profession: isUnknown ? null : profession,
        residence: isUnknown ? null : residence,
        nationalNumber: isUnknown ? null : nationalNumber,
        raavisNumber: isUnknown ? null : normalizedRaavisNumber,
        photographies: isUnknown ? '' : photographies,
        empreintes: isUnknown ? '' : empreintes,
        noticeIndividuelle: isUnknown ? '' : noticeIndividuelle
      };

      const endpoint = `/personnes-physiques${personneMode === 'edit' ? `/${personneItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = personneMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'personnesPhysiques',
        id: personneItemId,
        timestamp: Date.now()
      };
      
      if (personneMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: personneData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.personnesPhysiques];
          const index = updatedItems.findIndex(item => item._id === personneItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...personneData
            };
          }
          return {
            ...prevData,
            personnesPhysiques: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: personneData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de personne physique
      const logActionType = personneMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_PERSONNE_ADD 
        : LOG_ACTIONS.ELEMENTS_PERSONNE_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: personneMode,
        personne: personneData
      });
      
      handleClosePersonneDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${personneMode === 'add' ? 'ajout' : 'édition'} de la personne:`, error);
    }
  };

  const handleCopyPersonneNom = (personne) => {
    if (personne.isUnknown) return;
    
    const lastname = personne.lastname?.toUpperCase() || '';
    const firstname = personne.firstname || '';
    const textToCopy = `${lastname} ${firstname}`.trim();
    
    const parts = [];
    if (lastname) {
      parts.push(`<strong><u>${escapeHtml(lastname)}</u></strong>`);
    }
    if (firstname) {
      const separator = parts.length > 0 ? ' ' : '';
      parts.push(`${separator}<strong>${escapeHtml(firstname)}</strong>`);
    }
    const htmlContent = parts.join('');
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_nom',
        ...contentLengthLog(textToCopy)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  const handleCopyPersonneInfo = (personne) => {
    if (personne.isUnknown) return;
    
    const genderLabel = personne.gender === 'M' ? 'Masculin' : personne.gender === 'F' ? 'Féminin' : '';
    const birthdateLabel = personne.birthdate ? formatBirthdate(personne.birthdate) : '';
    const leftCellSections = [];
    
    if (personne.qualite) {
      leftCellSections.push(
        `<div style="margin-bottom:4px;"><strong>${escapeHtml(personne.qualite)}</strong></div>`
      );
    }
    
    if (personne.additionalQualities && personne.additionalQualities.length > 0) {
      leftCellSections.push(
        `<div style="margin-top:4px;"><em>${escapeHtml(personne.additionalQualities.join(', '))}</em></div>`
      );
    }
    
    const nameParts = [];
    if (personne.lastname) {
      nameParts.push(`<strong><u>${escapeHtml(personne.lastname.toUpperCase())}</u></strong>`);
    }
    if (personne.firstname) {
      nameParts.push(` <strong>${escapeHtml(personne.firstname)}</strong>`);
    }
    if (personne.otherFirstnames) {
      nameParts.push(` ${escapeHtml(personne.otherFirstnames)}`);
    }
    
    const rightCellSections = [];
    
    if (nameParts.length > 0) {
      rightCellSections.push(`<div style="margin-bottom:6px;">${nameParts.join('').trim()}</div>`);
    }
    
    if (genderLabel) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Sexe : ${genderLabel}</div>`
      );
    }
    
    if (birthdateLabel) {
      let birthLine = `Né le ${birthdateLabel}`;
      if (personne.birthPlace) {
        birthLine += ` à ${escapeHtml(personne.birthPlace)}`;
      }
      rightCellSections.push(`<div style="margin-bottom:4px;">${birthLine}</div>`);
    }
    
    if (personne.civilStatus) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">État civil : ${escapeHtml(personne.civilStatus)}</div>`
      );
    }
    
    if (personne.nationality) {
      let natLine = `Nationalité : ${escapeHtml(personne.nationality)}`;
      if (personne.nationalNumber) {
        natLine += ` (RRN : ${escapeHtml(personne.nationalNumber)})`;
      }
      rightCellSections.push(`<div style="margin-bottom:4px;">${natLine}</div>`);
    } else if (personne.nationalNumber) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">RRN : ${escapeHtml(personne.nationalNumber)}</div>`
      );
    }
    
    if (personne.profession) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Profession : ${escapeHtml(personne.profession)}</div>`
      );
    }
    
    if (personne.residence) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Domicile : ${escapeHtml(personne.residence)}</div>`
      );
    }
    
    if (personne.raavisNumber) {
      const statusParts = [];
      const formatStatusSymbol = (label, value) => {
        if (!value || value.toLowerCase() === 'ne pas mentionner') return null;
        const normalized = value.toLowerCase();
        if (normalized === 'oui') return `${label} <strong>&#10003;</strong>`;
        if (normalized === 'non') return `${label} <strong>&#10007;</strong>`;
        return null;
      };
      
      const empreintes = formatStatusSymbol('Empreintes', personne.empreintes || '');
      const photographies = formatStatusSymbol('Photographies', personne.photographies || '');
      const notice = formatStatusSymbol('Notice individuelle', personne.noticeIndividuelle || '');
      
      [empreintes, photographies, notice].forEach(status => {
        if (status) statusParts.push(status);
      });
      
      const suffix = statusParts.length ? ` (${statusParts.join(' - ')})` : '';
      
      rightCellSections.push(
        `<div style="margin-bottom:4px;">N° APFIS : <strong>${escapeHtml(personne.raavisNumber)}</strong>${suffix}</div>`
      );
    }
    
    const leftCellContent = leftCellSections.length
      ? `<div style="display:flex; flex-direction:column; justify-content:flex-start; align-items:flex-start; text-align:left; gap:4px; width:100%;">${leftCellSections.join('')}</div>`
      : '&nbsp;';
    
    const tableHtml = `
      <table style="width:100%; border-collapse:separate; border-spacing:0; background-color:transparent; table-layout:fixed;">
        <colgroup>
          <col style="width:130px; max-width:130px; min-width:130px;" />
          <col style="width:auto;" />
        </colgroup>
        <tbody>
          <tr>
            <td width="130" valign="top" style="width:130px; min-width:130px; max-width:130px; vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${leftCellContent}
            </td>
            <td valign="top" style="vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${rightCellSections.length ? rightCellSections.join('') : '&nbsp;'}
            </td>
          </tr>
        </tbody>
      </table>
    `;
    
    const tableHtmlContent = tableHtml.trim();

    const textSections = [];
    if (personne.qualite) {
      textSections.push(`Qualité\n${personne.qualite}`);
    }
    if (personne.additionalQualities && personne.additionalQualities.length > 0) {
      textSections.push(`Qualités additionnelles\n${personne.additionalQualities.join(', ')}`);
    }
    if (nameParts.length > 0) {
      textSections.push(
        `${personne.lastname?.toUpperCase() || ''} ${personne.firstname || ''}${personne.otherFirstnames ? ` (${personne.otherFirstnames})` : ''}`.trim()
      );
    }
    if (genderLabel) {
      textSections.push(`Sexe : ${genderLabel}`);
    }
    if (birthdateLabel) {
      let birthText = `Né le ${birthdateLabel}`;
      if (personne.birthPlace) {
        birthText += ` à ${personne.birthPlace}`;
      }
      textSections.push(birthText);
    }
    if (personne.civilStatus) {
      textSections.push(`État civil : ${personne.civilStatus}`);
    }
    if (personne.nationality) {
      let natText = `Nationalité : ${personne.nationality}`;
      if (personne.nationalNumber) {
        natText += ` (RRN : ${personne.nationalNumber})`;
      }
      textSections.push(natText);
    } else if (personne.nationalNumber) {
      textSections.push(`RRN : ${personne.nationalNumber}`);
    }
    if (personne.profession) {
      textSections.push(`Profession : ${personne.profession}`);
    }
    if (personne.residence) {
      textSections.push(`Domicile : ${personne.residence}`);
    }
    if (personne.raavisNumber) {
      const statusTextParts = [];
      const toSymbol = (value) => {
        if (!value || value.toLowerCase() === 'ne pas mentionner') return null;
        const normalized = value.toLowerCase();
        if (normalized === 'oui') return '✓';
        if (normalized === 'non') return '✗';
        return null;
      };
      const empreintesSymbol = toSymbol(personne.empreintes || '');
      const photosSymbol = toSymbol(personne.photographies || '');
      const noticeSymbol = toSymbol(personne.noticeIndividuelle || '');
      
      if (empreintesSymbol) statusTextParts.push(`Empreintes ${empreintesSymbol}`);
      if (photosSymbol) statusTextParts.push(`Photographies ${photosSymbol}`);
      if (noticeSymbol) statusTextParts.push(`Notice individuelle ${noticeSymbol}`);
      
      const statusText = statusTextParts.length ? ` (${statusTextParts.join(' - ')})` : '';
      textSections.push(`N° APFIS : ${personne.raavisNumber}${statusText}`);
    }
    
    const textToCopy = textSections.join('\n');
    
    const notificationLabel = `${personne.lastname?.toUpperCase() || ''} ${personne.firstname || ''}`.trim() || 'Informations copiées';
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_info',
        ...contentLengthLog(textToCopy)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([tableHtmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(notificationLabel);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, tableHtmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, tableHtmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les objets
  const resetObjetForm = () => {
    setDescription('');
    setSinNumber('');
    setObjetErrors({});
  };

  const handleOpenObjetDialog = (mode = 'add', itemId = null) => {
    setObjetMode(mode);
    setObjetItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const objet = reportData.objets.find(item => item._id === itemId);
      if (objet) {
        setDescription(objet.description);
        setSinNumber(objet.sinNumber || '');
      }
    } else {
      resetObjetForm();
    }
    
    setOpenObjetDialog(true);
  };

  const handleCloseObjetDialog = () => {
    setOpenObjetDialog(false);
    resetObjetForm();
  };

  const validateObjetForm = () => {
    const errors = {};
    
    if (!description) {
      errors.description = 'La description est obligatoire';
    }
    
    setObjetErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveObjet = async () => {
    if (!validateObjetForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const objetData = {
        description,
        sinNumber
      };

      const endpoint = `/objets${objetMode === 'edit' ? `/${objetItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = objetMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'objets',
        id: objetItemId,
        timestamp: Date.now()
      };
      
      if (objetMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: objetData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.objets];
          const index = updatedItems.findIndex(item => item._id === objetItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...objetData
            };
          }
          return {
            ...prevData,
            objets: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: objetData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification d'objet
      const logActionType = objetMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_OBJET_ADD 
        : LOG_ACTIONS.ELEMENTS_OBJET_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: objetMode,
        objet: objetData
      });
      
      handleCloseObjetDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${objetMode === 'add' ? 'ajout' : 'édition'} de l'objet:`, error);
    }
  };

  const handleCopyObjet = (objet) => {
    if (!objet?.description) return;

    let textToCopy = objet.description;
    if (objet.sinNumber) {
      textToCopy += ` (SIN ${objet.sinNumber})`;
    }

    const htmlContent = objet.sinNumber
      ? `${escapeHtml(objet.description)} (<strong>SIN ${escapeHtml(objet.sinNumber)}</strong>)`
      : null;

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'objet',
        ...contentLengthLog(textToCopy)
      });
    };

    if (objet.sinNumber && navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => { logCopy(); showCopyNotification(textToCopy); })
        .catch(() => { handleCopyToClipboard(textToCopy, htmlContent); logCopy(); });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les véhicules
  const resetVehiculeForm = () => {
    setBrand('');
    setVehiculeCategory('');
    setPlateNumber('');
    setChassisNumber('');
    setVehiculeNationality('');
    setVehiculeColor('');
    setInsurance('');
    setTechnicalControl('');
    setVehiculeErrors({});
  };

  const handleOpenVehiculeDialog = (mode = 'add', itemId = null) => {
    setVehiculeMode(mode);
    setVehiculeItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const vehicule = reportData.vehicules.find(item => item._id === itemId);
      if (vehicule) {
        setBrand(vehicule.brand || '');
        setVehiculeCategory(vehicule.category || '');
        setPlateNumber((vehicule.plateNumber || '').toUpperCase());
        setChassisNumber((vehicule.chassisNumber || '').toUpperCase());
        setVehiculeNationality(vehicule.nationality || '');
        setVehiculeColor(vehicule.color || '');
        setInsurance(vehicule.insurance || '');
        setTechnicalControl(vehicule.technicalControl || '');
      }
    } else {
      resetVehiculeForm();
    }
    
    setOpenVehiculeDialog(true);
  };

  const handleCloseVehiculeDialog = () => {
    setOpenVehiculeDialog(false);
    resetVehiculeForm();
  };

  const validateVehiculeForm = () => {
    const errors = {};
    
    if (!brand) {
      errors.brand = 'La marque/modèle est obligatoire';
    }
    
    setVehiculeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveVehicule = async () => {
    if (!validateVehiculeForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const vehiculeData = {
        brand,
        category: vehiculeCategory,
        plateNumber: plateNumber.toUpperCase(),
        chassisNumber: chassisNumber.toUpperCase(),
        nationality: vehiculeNationality,
        color: vehiculeColor,
        insurance,
        technicalControl
      };

      const endpoint = `/vehicules${vehiculeMode === 'edit' ? `/${vehiculeItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = vehiculeMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'vehicules',
        id: vehiculeItemId,
        timestamp: Date.now()
      };
      
      if (vehiculeMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: vehiculeData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.vehicules];
          const index = updatedItems.findIndex(item => item._id === vehiculeItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...vehiculeData
            };
          }
          return {
            ...prevData,
            vehicules: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: vehiculeData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de véhicule
      const logActionType = vehiculeMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_VEHICULE_ADD 
        : LOG_ACTIONS.ELEMENTS_VEHICULE_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: vehiculeMode,
        vehicule: vehiculeData
      });
      
      handleCloseVehiculeDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${vehiculeMode === 'add' ? 'ajout' : 'édition'} du véhicule:`, error);
    }
  };

  const handleCopyVehiculeSimple = (vehicule) => {
    if (!vehicule?.brand) return;
    
    const baseText = vehicule.brand;
    const plateText = vehicule.plateNumber ? ` immatriculé ${vehicule.plateNumber}` : '';
    const textToCopy = `${baseText}${plateText}`.trim();
    const htmlContent = vehicule.plateNumber
      ? `${escapeHtml(baseText)} immatriculé <strong>${escapeHtml(vehicule.plateNumber)}</strong>`
      : null;

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'vehicule_simple',
        ...contentLengthLog(textToCopy)
      });
    };

    if (vehicule.plateNumber && navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch(() => {
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  const handleCopyVehiculeInfo = (vehicule) => {
    if (!vehicule) return;

    const rightCellSections = [];
    const textSections = [];

    if (vehicule.brand) {
      rightCellSections.push(`<div style="margin-bottom:4px;"><strong><u>${escapeHtml(vehicule.brand)}</u></strong></div>`);
      textSections.push(vehicule.brand);
    }

    if (vehicule.plateNumber) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Immatriculation : <strong>${escapeHtml(vehicule.plateNumber)}</strong></div>`);
      textSections.push(`Immatriculation : ${vehicule.plateNumber}`);
    }

    if (vehicule.chassisNumber) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Châssis : <strong>${escapeHtml(vehicule.chassisNumber)}</strong></div>`);
      textSections.push(`Châssis : ${vehicule.chassisNumber}`);
    }

    if (vehicule.nationality) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Nationalité : ${escapeHtml(vehicule.nationality)}</div>`);
      textSections.push(`Nationalité : ${vehicule.nationality}`);
    }

    if (vehicule.color) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Teinte : ${escapeHtml(vehicule.color)}</div>`);
      textSections.push(`Teinte : ${vehicule.color}`);
    }

    if (vehicule.insurance) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Assurance : ${escapeHtml(vehicule.insurance)}</div>`);
      textSections.push(`Assurance : ${vehicule.insurance}`);
    }

    if (vehicule.technicalControl) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Contrôle technique : ${escapeHtml(vehicule.technicalControl)}</div>`);
      textSections.push(`Contrôle technique : ${vehicule.technicalControl}`);
    }

    const leftCellContent = vehicule.category
      ? `<div style="display:flex; flex-direction:column; justify-content:flex-start; align-items:flex-start; text-align:left; gap:4px; width:100%;"><strong>${escapeHtml(vehicule.category)}</strong></div>`
      : '&nbsp;';

    const tableHtml = `
      <table style="width:100%; border-collapse:separate; border-spacing:0; background-color:transparent; table-layout:fixed;">
        <colgroup>
          <col style="width:130px; max-width:130px; min-width:130px;" />
          <col style="width:auto;" />
        </colgroup>
        <tbody>
          <tr>
            <td width="130" valign="top" style="width:130px; min-width:130px; max-width:130px; vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${leftCellContent}
            </td>
            <td valign="top" style="vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${rightCellSections.length ? rightCellSections.join('') : '&nbsp;'}
            </td>
          </tr>
        </tbody>
      </table>
    `.trim();

    if (vehicule.category) {
      textSections.unshift(vehicule.category);
    }

    const textToCopy = textSections.join('\n');
    const notificationLabel = vehicule.brand || vehicule.category || 'Véhicule copié';

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'vehicule_info',
        ...contentLengthLog(textToCopy)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([tableHtml], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => { logCopy(); showCopyNotification(notificationLabel); })
        .catch(() => { handleCopyToClipboard(textToCopy, tableHtml); logCopy(); });
    } else {
      handleCopyToClipboard(textToCopy, tableHtml);
      logCopy();
    }
  };

  // Fonctions pour gérer les personnes morales
  const resetPersonneMoraleForm = () => {
    setPersonneMoraleQualite('');
    setDenomination('');
    setCompanyNumber('');
    setHeadquarters('');
    setPersonneMoraleErrors({});
  };

  const handleOpenPersonneMoraleDialog = (mode = 'add', itemId = null) => {
    setPersonneMoraleMode(mode);
    setPersonneMoraleItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const personneMorale = reportData.personnesMorales.find(item => item._id === itemId);
      if (personneMorale) {
        setPersonneMoraleQualite(personneMorale.qualite || '');
        setDenomination(personneMorale.denomination);
        setCompanyNumber(personneMorale.companyNumber || '');
        setHeadquarters(personneMorale.headquarters || '');
      }
    } else {
      resetPersonneMoraleForm();
    }
    
    setOpenPersonneMoraleDialog(true);
  };

  const handleClosePersonneMoraleDialog = () => {
    setOpenPersonneMoraleDialog(false);
    resetPersonneMoraleForm();
  };

  const validatePersonneMoraleForm = () => {
    const errors = {};
    
    if (!personneMoraleQualite) {
      errors.qualite = 'La qualité est obligatoire';
    }
    
    if (!denomination) {
      errors.denomination = 'La dénomination est obligatoire';
    }
    
    setPersonneMoraleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePersonneMorale = async () => {
    if (!validatePersonneMoraleForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const personneMoraleData = {
        qualite: personneMoraleQualite,
        denomination,
        companyNumber,
        headquarters
      };

      const endpoint = `/personnes-morales${personneMoraleMode === 'edit' ? `/${personneMoraleItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = personneMoraleMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'personnesMorales',
        id: personneMoraleItemId,
        timestamp: Date.now()
      };
      
      if (personneMoraleMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: personneMoraleData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.personnesMorales];
          const index = updatedItems.findIndex(item => item._id === personneMoraleItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...personneMoraleData
            };
          }
          return {
            ...prevData,
            personnesMorales: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: personneMoraleData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de personne morale
      const logActionType = personneMoraleMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_MORALE_ADD 
        : LOG_ACTIONS.ELEMENTS_MORALE_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: personneMoraleMode,
        personneMorale: personneMoraleData
      });
      
      handleClosePersonneMoraleDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${personneMoraleMode === 'add' ? 'ajout' : 'édition'} de la personne morale:`, error);
    }
  };

  const handleCopyPersonneMoraleNom = (personneMorale) => {
    if (!personneMorale?.denomination) return;
    
    const textToCopy = personneMorale.denomination;
    const htmlContent = `<strong><u>${escapeHtml(textToCopy)}</u></strong>`;
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_morale_nom',
        ...contentLengthLog(textToCopy)
      });
    };
    
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  const handleCopyPersonneMoraleInfo = (personneMorale) => {
    if (!personneMorale) return;
    
    const leftCellSections = [];
    if (personneMorale.qualite) {
      leftCellSections.push(
        `<div style="margin-bottom:4px;"><strong>${escapeHtml(personneMorale.qualite)}</strong></div>`
      );
    }
    
    const rightCellSections = [];
    if (personneMorale.denomination) {
      rightCellSections.push(
        `<div style="margin-bottom:6px;"><strong><u>${escapeHtml(personneMorale.denomination)}</u></strong></div>`
      );
    }
    
    if (personneMorale.companyNumber) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Numéro d'entreprise : ${escapeHtml(personneMorale.companyNumber)}</div>`
      );
    }
    
    if (personneMorale.headquarters) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Siège social : ${escapeHtml(personneMorale.headquarters)}</div>`
      );
    }
    
    const leftCellContent = leftCellSections.length
      ? `<div style="display:flex; flex-direction:column; justify-content:flex-start; align-items:flex-start; text-align:left; gap:4px; width:100%;">${leftCellSections.join('')}</div>`
      : '&nbsp;';
    
    const tableHtml = `
      <table style="width:100%; border-collapse:separate; border-spacing:0; background-color:transparent; table-layout:fixed;">
        <colgroup>
          <col style="width:130px; max-width:130px; min-width:130px;" />
          <col style="width:auto;" />
        </colgroup>
        <tbody>
          <tr>
            <td width="130" valign="top" style="width:130px; min-width:130px; max-width:130px; vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${leftCellContent}
            </td>
            <td valign="top" style="vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${rightCellSections.length ? rightCellSections.join('') : '&nbsp;'}
            </td>
          </tr>
        </tbody>
      </table>
    `;
    
    const tableHtmlContent = tableHtml.trim();
    
    const textSections = [];
    if (personneMorale.qualite) {
      textSections.push(`Qualité\n${personneMorale.qualite}`);
    }
    if (personneMorale.denomination) {
      textSections.push(personneMorale.denomination);
    }
    if (personneMorale.companyNumber) {
      textSections.push(`Numéro d'entreprise : ${personneMorale.companyNumber}`);
    }
    if (personneMorale.headquarters) {
      textSections.push(`Siège social : ${personneMorale.headquarters}`);
    }
    
    const textToCopy = textSections.join('\n');
    const notificationLabel = personneMorale.denomination || 'Personne morale copiée';
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_morale_info',
        ...contentLengthLog(textToCopy)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([tableHtmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(notificationLabel);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, tableHtmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, tableHtmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les faits
  const resetFaitForm = () => {
    setIsUndetermined(false);
    setIsQualified(false);
    setIsTentative(false);
    setFait('');
    setArticle('');
    setTimeType('fixed');
    setFixedDateTime('');
    setRangeLowDateTime('');
    setRangeHighDateTime('');
    setCountry('');
    setAddress('');
    setDestination('');
    setFaitErrors({});
  };

  const handleOpenFaitDialog = (mode = 'add', itemId = null) => {
    setFaitMode(mode);
    setFaitItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const faitItem = reportData.faits.find(item => item._id === itemId);
      if (faitItem) {
        setIsUndetermined(faitItem.isUndetermined || false);
        setIsQualified(faitItem.isQualified || false);
        setIsTentative(faitItem.isTentative || false);
        setFait(faitItem.fait || '');
        setArticle(faitItem.article || '');
        setTimeType(faitItem.timeType || 'fixed');
        setFixedDateTime(faitItem.fixedDateTime || '');
        setRangeLowDateTime(faitItem.rangeLowDateTime || '');
        setRangeHighDateTime(faitItem.rangeHighDateTime || '');
        setCountry(faitItem.country || '');
        setAddress(faitItem.address || '');
        setDestination(faitItem.destination || '');
      }
    } else {
      resetFaitForm();
    }
    
    setOpenFaitDialog(true);
  };

  const handleCloseFaitDialog = () => {
    setOpenFaitDialog(false);
    resetFaitForm();
  };

  const validateFaitForm = () => {
    const errors = {};
    
    if (!isUndetermined) {
      if (!fait) {
        errors.fait = 'Le fait est obligatoire';
      }
      if (!address) {
        errors.address = 'L\'adresse est obligatoire';
      }
    }
    
    setFaitErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveFait = async () => {
    if (!validateFaitForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const faitData = {
        isUndetermined,
        isQualified: isUndetermined ? false : isQualified,
        isTentative: isUndetermined ? false : isTentative,
        fait: isUndetermined ? '' : fait,
        article: isUndetermined ? '' : article,
        timeType: isUndetermined ? 'fixed' : timeType,
        fixedDateTime: isUndetermined ? '' : fixedDateTime,
        rangeLowDateTime: isUndetermined ? '' : rangeLowDateTime,
        rangeHighDateTime: isUndetermined ? '' : rangeHighDateTime,
      country: isUndetermined ? '' : country,
      address: isUndetermined ? '' : address,
      destination: isUndetermined ? '' : destination
      };

      const endpoint = `/faits${faitMode === 'edit' ? `/${faitItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = faitMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'faits',
        id: faitItemId,
        timestamp: Date.now()
      };
      
      if (faitMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: faitData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.faits];
          const index = updatedItems.findIndex(item => item._id === faitItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...faitData
            };
          }
          return {
            ...prevData,
            faits: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: faitData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de fait
      const logActionType = faitMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_FAIT_ADD 
        : LOG_ACTIONS.ELEMENTS_FAIT_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: faitMode,
        fait: faitData
      });
      
      handleCloseFaitDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${faitMode === 'add' ? 'ajout' : 'édition'} du fait:`, error);
    }
  };

  const handleCopyFait = (faitItem) => {
    if (faitItem.isUndetermined) {
      const textContent = 'Fait non déterminé - procès-verbal subséquent';
      const htmlContent = `<strong>${escapeHtml(textContent)}</strong>`;
      
      const logCopy = () => {
        logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
          type: 'fait',
          ...contentLengthLog(textContent)
        });
      };
      
      // Copier avec format HTML et texte
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        });
        
        navigator.clipboard.write([clipboardItem])
          .then(() => {
            logCopy();
            showCopyNotification(textContent);
          })
          .catch((error) => {
            console.error('Erreur lors de la copie:', error);
            handleCopyToClipboard(textContent, htmlContent);
            logCopy();
          });
      } else {
        handleCopyToClipboard(textContent, htmlContent);
        logCopy();
      }
      return;
    }
    
    let htmlLines = [];
    let textLines = [];
    const indent = '&nbsp;&nbsp;&nbsp;&nbsp;';
    
    // Ligne 1 - En gras
    const line1 = formatFaitHeading(faitItem);
    htmlLines.push(`<strong>${escapeHtml(line1)}</strong>`);
    textLines.push(line1);
    
    // Ligne 2 si article rempli - avec tabulation
    if (faitItem.article) {
      const line2 = `Article : ${faitItem.article}`;
      htmlLines.push(`${indent}${escapeHtml(line2)}`);
      textLines.push(`\t${line2}`);
    }
    
    // Ligne 3 - Date/heure - avec tabulation
    const line3 = getFaitDateDescription(faitItem);
    htmlLines.push(`${indent}${escapeHtml(line3)}`);
    textLines.push(`\t${line3}`);
    
    // Ligne 4 - Adresse - avec tabulation
    const baseAddress = faitItem.address || '';
    const baseCountry = faitItem.country || '';
    const destinationPart = faitItem.destination ? ` (${faitItem.destination})` : '';
    let addressPart = '';
    if (baseCountry && baseAddress) {
      addressPart = `${baseCountry}, ${baseAddress}${destinationPart}`;
    } else if (baseCountry || baseAddress) {
      addressPart = `${baseCountry}${baseCountry && baseAddress ? ', ' : ''}${baseAddress}${destinationPart}`;
    } else if (faitItem.destination) {
      addressPart = faitItem.destination;
    }
    if (addressPart) {
      const line4 = `À ${addressPart}`;
      htmlLines.push(`${indent}${escapeHtml(line4)}`);
      textLines.push(`\t${line4}`);
    }
    
    // Copier à la fois HTML et texte brut
    const htmlContent = htmlLines.join('<br>');
    const textContent = textLines.join('\n');
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'fait',
        ...contentLengthLog(textContent)
      });
    };

    // Utiliser l'API Clipboard pour copier du HTML
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textContent], { type: 'text/plain' })
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textContent);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          // Fallback sur texte simple
          handleCopyToClipboard(textContent, htmlContent);
          logCopy();
        });
    } else {
      // Fallback pour les navigateurs plus anciens
      handleCopyToClipboard(textContent, htmlContent);
      logCopy();
    }
  };

  // Ajouter une fonction pour recharger les données du rapport
  const reloadReportData = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setReportData(response.data);
      setUnit(response.data.unit || '');
    } catch (error) {
      console.error('Erreur lors du rechargement des données du rapport:', error);
    }
  };

  // Fonction pour formater la date et heure de début (StartDate)
  const formatStartDate = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Format de date: jour mois année
      const dateFormatee = date.toLocaleDateString('fr-FR', {
        weekday: "long",
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      // Heure et minutes au format HH:MM
      const heures = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const heureFormatee = `${heures}:${minutes}`;
      
      // Ajouter un "s" à "heure" si l'heure est 2 ou plus
      const pluriel = date.getHours() >= 2 ? 's' : '';
      
      return `${dateFormatee} à ${heureFormatee} heure${pluriel}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date de début:', error);
      return dateString;
    }
  };

  // Fonction pour formater la date de fin (EndDate)
  const formatEndDate = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Format de date: jour mois année
      return date.toLocaleDateString('fr-FR', {
        weekday: "long",
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Erreur lors du formatage de la date de fin:', error);
      return dateString;
    }
  };

  // Fonction pour formater la date de naissance (birthdate)
  const formatBirthdate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date de naissance:', error);
      return dateString;
    }
  };

  // Fonction pour calculer l'âge à partir de la date de naissance
  const calculateAge = (dateString) => {
    if (!dateString) return '';
    
    try {
      const birthDate = new Date(dateString);
      if (isNaN(birthDate.getTime())) return '';
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age >= 0 ? age : '';
    } catch (error) {
      console.error('Erreur lors du calcul de l\'âge:', error);
      return '';
    }
  };

  const formatGenderAgeLabel = (gender, birthdate) => {
    const genderLabel = gender === 'M' ? 'Masculin' : gender === 'F' ? 'Féminin' : '';
    const ageValue = birthdate ? calculateAge(birthdate) : '';
    if (!genderLabel && (ageValue === '' || ageValue === null || ageValue === undefined)) {
      return '';
    }
    if (ageValue === '' || ageValue === null || ageValue === undefined) {
      return genderLabel;
    }
    const ageNumber = Number(ageValue);
    const plural = ageNumber > 1 ? 'ans' : 'an';
    return genderLabel ? `${genderLabel} - ${ageNumber} ${plural}` : `${ageNumber} ${plural}`;
  };

  // Fonction pour formater la date et heure pour les faits
  const formatFaitDateTime = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Jour de la semaine
      const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' });
      
      // Date au format JJ mois AAAA
      const day = date.getDate();
      const month = date.toLocaleDateString('fr-FR', { month: 'long' });
      const year = date.getFullYear();
      
      // Heure au format HH:MM
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const hoursFormatted = hours.toString().padStart(2, '0');
      
      // Ajouter un "s" à "heure" si l'heure est 2 ou plus
      const pluriel = hours >= 2 ? 's' : '';
      
      return `${dayName} ${day} ${month} ${year} à ${hoursFormatted}:${minutes} heure${pluriel}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date/heure du fait:', error);
      return dateString;
    }
  };

  const formatFaitTimeLabel = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const hoursFormatted = hours.toString().padStart(2, '0');
    const pluriel = hours >= 2 ? 's' : '';
    return `${hoursFormatted}:${minutes} heure${pluriel}`;
  };

  const formatFaitSameDayRange = (lowDateString, highDateString) => {
    if (!lowDateString || !highDateString) return null;
    try {
      const start = new Date(lowDateString);
      const end = new Date(highDateString);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

      const isSameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();

      if (!isSameDay) return null;

      const dateLabel = start.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      return `Le ${dateLabel} entre ${formatFaitTimeLabel(start)} et ${formatFaitTimeLabel(end)}`;
    } catch (error) {
      console.error('Erreur lors du formatage de l\'intervalle du fait:', error);
      return null;
    }
  };

  const getFaitDateDescription = (faitItem) => {
    if (faitItem.timeType === 'range') {
      const sameDayRangeDescription = formatFaitSameDayRange(
        faitItem.rangeLowDateTime,
        faitItem.rangeHighDateTime
      );
      if (sameDayRangeDescription) {
        return sameDayRangeDescription;
      }
      const lowDate = formatFaitDateTime(faitItem.rangeLowDateTime);
      const highDate = formatFaitDateTime(faitItem.rangeHighDateTime);
      return `Entre le ${lowDate} et le ${highDate}`;
    }
    const fixedDate = formatFaitDateTime(faitItem.fixedDateTime);
    return `Le ${fixedDate}`;
  };

  const escapeHtml = (text) => {
    if (text === null || text === undefined) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Contenu affiché pour chaque élément encodé, également parcouru par le filtre
  const describeItem = (sectionKey, item) => {
    switch (sectionKey) {
      case 'intervenants':
        return {
          primary: `${item.lastname?.toUpperCase() || ''} ${item.firstname || ''}`.trim() || 'Intervenant',
          tags: [
            item.sign && { label: 'Signataire', tone: 'accent' },
            item.isOPJ && { label: 'OPJ', tone: 'muted' },
            item.isOPA && { label: 'OPA', tone: 'muted' },
            item.isExternal && { label: 'Membre extérieur', tone: 'muted' }
          ],
          lines: [{ text: convertRankAcronym(item.rank) }]
        };
      case 'faits': {
        if (item.isUndetermined) {
          return {
            primary: 'Fait non déterminé',
            tags: [{ label: 'PV subséquent', tone: 'muted' }]
          };
        }
        const hasPlace = item.country || item.address || item.destination;
        return {
          primary: formatFaitHeading(item) || 'Fait',
          lines: [
            item.article && { icon: MenuBookOutlinedIcon, text: `Article : ${item.article}` },
            { icon: ScheduleIcon, text: getFaitDateDescription(item) },
            hasPlace && {
              icon: PlaceOutlinedIcon,
              text: `À ${item.country || ''}${item.country && item.address ? ', ' : ''}${item.address || ''}${item.destination ? ` (${item.destination})` : ''}`
            }
          ]
        };
      }
      case 'personnesPhysiques': {
        const qualities = [
          item.qualite && { label: item.qualite, tone: 'accent' },
          ...(item.additionalQualities || []).map((quality) => ({ label: quality, tone: 'warning' }))
        ];
        if (item.isUnknown) {
          return { primary: 'Individu inconnu', muted: true, tags: qualities };
        }
        const born = item.gender === 'M' ? 'Né' : item.gender === 'F' ? 'Née' : 'Né(e)';
        const birth = item.birthdate
          ? `Ddn : ${formatBirthdate(item.birthdate)}${item.birthPlace ? ` à ${item.birthPlace}` : ''}`
          : item.birthPlace && `${born} à ${item.birthPlace}`;
        return {
          primary: `${item.lastname?.toUpperCase() || ''}${item.firstname ? ` ${item.firstname}` : ''}`.trim() || 'Personne physique',
          note: item.otherFirstnames,
          tags: qualities,
          lines: [
            { text: formatGenderAgeLabel(item.gender, item.birthdate) },
            { text: birth }
          ],
          details: [
            { label: 'État civ.', value: item.civilStatus },
            { label: 'Nat.', value: item.nationality },
            { label: 'Prof.', value: item.profession },
            { label: 'Dom.', value: item.residence },
            { label: 'NN', value: item.nationalNumber, mono: true },
            { label: 'Apfis', value: item.raavisNumber, mono: true }
          ],
          footer: [
            ['Photo', item.photographies],
            ['Empr.', item.empreintes],
            ['Not.', item.noticeIndividuelle]
          ]
            .filter(([, value]) => value)
            .map(([label, value]) => ({ label: `${label} : ${value}`, tone: value === 'Oui' ? 'success' : 'muted' }))
        };
      }
      case 'personnesMorales':
        return {
          primary: item.denomination || 'Personne morale',
          tags: [item.qualite && { label: item.qualite, tone: 'accent' }],
          details: [
            { label: 'N° entreprise', value: item.companyNumber, mono: true },
            { label: 'Siège', value: item.headquarters }
          ]
        };
      case 'vehicules':
        return {
          primary: item.brand || 'Véhicule',
          tags: [item.category && { label: item.category, tone: 'muted' }],
          details: [
            { label: 'Immat.', value: item.plateNumber, mono: true },
            { label: 'Châssis', value: item.chassisNumber, mono: true },
            { label: 'Nat.', value: item.nationality },
            { label: 'Teinte', value: item.color },
            { label: 'Assurance', value: item.insurance },
            { label: 'CT', value: item.technicalControl }
          ]
        };
      case 'objets':
      default:
        return {
          primary: item.description || 'Objet',
          details: [{ label: 'SIN', value: item.sinNumber, mono: true }]
        };
    }
  };

  const getItemView = (sectionKey, item) => {
    const view = describeItem(sectionKey, item);
    const tags = (view.tags || []).filter(Boolean);
    const lines = (view.lines || []).filter((line) => line && line.text);
    const details = (view.details || []).filter((detail) => detail.value);
    const footer = view.footer || [];
    const fields = [
      view.primary,
      view.note,
      ...tags.map((tag) => tag.label),
      ...lines.map((line) => line.text),
      ...details.map((detail) => detail.value),
      ...footer.map((tag) => tag.label)
    ].filter(Boolean);
    return {
      ...view,
      tags,
      lines,
      details,
      footer,
      searchText: norm(fields.join(' ')),
      compactFields: fields.map(compact)
    };
  };

  const query = norm(filterText);
  const compactQuery = compact(filterText);

  // Les numéros (NN, plaque, châssis…) se retrouvent aussi sans leur ponctuation
  const matchesFilter = (view) => view.searchText.includes(query) ||
    (compactQuery.length > 1 && view.compactFields.some((field) => field.includes(compactQuery)));

  const sectionActions = {
    intervenants: {
      add: () => handleOpenIntervenantDialog(),
      edit: (item) => handleOpenIntervenantDialog('edit', item._id),
      copies: (item) => [
        { title: 'Copier dans le presse-papier', icon: ContentCopyIcon, onClick: () => handleCopyIntervenant(item) }
      ]
    },
    faits: {
      add: () => handleOpenFaitDialog(),
      edit: (item) => handleOpenFaitDialog('edit', item._id),
      copies: (item) => [
        { title: 'Copier dans le presse-papier', icon: ContentCopyIcon, onClick: () => handleCopyFait(item) }
      ]
    },
    personnesPhysiques: {
      add: () => handleOpenPersonneDialog(),
      edit: (item) => handleOpenPersonneDialog('edit', item._id),
      copies: (item) => (item.isUnknown ? [] : [
        { title: 'Copier le nom', icon: PersonIcon, onClick: () => handleCopyPersonneNom(item) },
        { title: 'Copier toutes les infos', icon: ContentCopyIcon, onClick: () => handleCopyPersonneInfo(item) }
      ])
    },
    personnesMorales: {
      add: () => handleOpenPersonneMoraleDialog(),
      edit: (item) => handleOpenPersonneMoraleDialog('edit', item._id),
      copies: (item) => [
        { title: 'Copier le nom', icon: BusinessIcon, onClick: () => handleCopyPersonneMoraleNom(item) },
        { title: 'Copier toutes les infos', icon: ContentCopyIcon, onClick: () => handleCopyPersonneMoraleInfo(item) }
      ]
    },
    vehicules: {
      add: () => handleOpenVehiculeDialog(),
      edit: (item) => handleOpenVehiculeDialog('edit', item._id),
      copies: (item) => [
        { title: 'Copier le véhicule', icon: DirectionsCarIcon, onClick: () => handleCopyVehiculeSimple(item) },
        { title: 'Copier toutes les infos', icon: ContentCopyIcon, onClick: () => handleCopyVehiculeInfo(item) }
      ]
    },
    objets: {
      add: () => handleOpenObjetDialog(),
      edit: (item) => handleOpenObjetDialog('edit', item._id),
      copies: (item) => [
        { title: 'Copier dans le presse-papier', icon: ContentCopyIcon, onClick: () => handleCopyObjet(item) }
      ]
    }
  };

  const sectionTitleSx = {
    color: c.title,
    fontWeight: isDark ? 600 : 700,
    textTransform: isDark ? 'none' : 'uppercase',
    letterSpacing: isDark ? 'normal' : '0.03em',
    fontSize: isDark ? '0.875rem' : '0.72rem'
  };

  const cardSx = {
    backgroundColor: c.surface,
    backgroundImage: 'none',
    border: isDark ? 'none' : `1px solid ${c.border}`,
    borderRadius: '10px',
    overflow: 'hidden'
  };

  const iconBadgeSx = (active, size = 26) => ({
    width: size,
    height: size,
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    backgroundColor: active ? c.accentSoft : c.mutedSoft,
    color: active ? c.accent : c.textSecondary,
    transition: 'background-color 0.2s ease, color 0.2s ease'
  });

  // Bouton d'en-tête (Ajouter / Modifier) : le libellé disparaît quand la colonne devient trop étroite
  const tonalButtonSx = {
    flexShrink: 0,
    minWidth: 0,
    minHeight: 26,
    py: 0.25,
    px: 1,
    borderRadius: '8px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: c.accent,
    backgroundColor: c.accentSoft,
    '& .MuiButton-startIcon': { mr: 0.5, ml: -0.25 },
    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 16 },
    '&:hover': { backgroundColor: c.accentSoftHover },
    '@container encodage (max-width: 280px)': {
      px: 0.75,
      '& .tonal-label': { display: 'none' },
      '& .MuiButton-startIcon': { m: 0 }
    }
  };

  const actionButtonSx = (tone) => ({
    p: '5px',
    borderRadius: '7px',
    color: c.textSecondary,
    '&:hover': {
      color: tone === 'danger' ? c.danger : c.accent,
      backgroundColor: tone === 'danger' ? c.dangerSoft : c.accentSoft
    }
  });

  const toneColors = {
    accent: [c.accentSoft, c.accent],
    warning: [c.warningSoft, c.warning],
    success: [c.successSoft, c.success],
    muted: [c.mutedSoft, c.textSecondary]
  };

  const renderTag = ({ label, tone = 'accent' }) => (
    <Chip
      key={label}
      label={label}
      size="small"
      sx={{
        height: 20,
        maxWidth: '100%',
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: toneColors[tone][0],
        color: toneColors[tone][1],
        '& .MuiChip-label': { px: 0.75 }
      }}
    />
  );

  const renderCount = (value, active) => (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        minWidth: 20,
        height: 18,
        px: 0.75,
        borderRadius: '9px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7rem',
        fontWeight: 700,
        backgroundColor: active ? c.accentSoft : c.mutedSoft,
        color: active ? c.accent : c.textSecondary
      }}
    >
      {value}
    </Box>
  );

  const renderAction = ({ title, icon: Icon, onClick, tone, disabled = false, sx }) => (
    <Tooltip key={title} title={title}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <IconButton
          size="small"
          aria-label={title}
          onClick={onClick}
          disabled={disabled}
          sx={{ ...actionButtonSx(tone), ...sx }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>
    </Tooltip>
  );

  const renderSectionHeader = ({ key, label, icon: Icon, count, active, action }) => {
    const filtering = Boolean(query) && key !== 'general';
    const expanded = filtering || sectionExpanded[key];
    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.75,
          backgroundColor: c.panel
        }}
      >
        <ButtonBase
          onClick={() => toggleSection(key)}
          disabled={filtering}
          aria-expanded={expanded}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 0.75,
            py: 0.25,
            pl: 0.25,
            pr: 0.75,
            borderRadius: '8px',
            textAlign: 'left',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: c.hover },
            '&.Mui-focusVisible': { outline: `2px solid ${c.accent}`, outlineOffset: '-2px' }
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: c.textSecondary,
              opacity: filtering ? 0.35 : 1,
              transform: expanded ? 'none' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease'
            }}
          />
          <Box component="span" sx={iconBadgeSx(active)}>
            <Icon sx={{ fontSize: 16 }} />
          </Box>
          <Typography component="span" variant="body2" noWrap sx={{ ...sectionTitleSx, minWidth: 0 }}>
            {label}
          </Typography>
          {count !== undefined && renderCount(count, active)}
        </ButtonBase>
        {action}
      </Box>
    );
  };

  const renderItemRow = (sectionKey, item, view) => {
    const actions = sectionActions[sectionKey];
    const rowActions = [
      ...actions.copies(item),
      { title: 'Modifier', icon: EditOutlinedIcon, onClick: () => actions.edit(item) },
      { title: 'Supprimer', icon: DeleteOutlineIcon, tone: 'danger', onClick: () => handleOpenDeleteDialog(sectionKey, item._id) }
    ];

    return (
      <Box
        sx={{
          position: 'relative',
          px: 1.25,
          py: 1,
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: c.hover }
        }}
      >
        <Box sx={{ position: 'absolute', top: 5, right: 4, display: 'flex' }}>
          {rowActions.map(renderAction)}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 0.75,
            rowGap: 0.5,
            minHeight: 24,
            pr: `${rowActions.length * 27}px`
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.35,
              wordBreak: 'break-word',
              color: view.muted ? c.textSecondary : c.text,
              fontStyle: view.muted ? 'italic' : 'normal'
            }}
          >
            {view.primary}
            {view.note && (
              <Box component="span" sx={{ ml: 0.5, fontWeight: 400, fontStyle: 'italic', color: c.textSecondary }}>
                {view.note}
              </Box>
            )}
          </Typography>
          {view.tags.map(renderTag)}
        </Box>

        {view.lines.map(({ icon: LineIcon, text }, index) => (
          <Box key={`${index}-${text}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25 }}>
            {LineIcon && <LineIcon sx={{ fontSize: 14, color: c.textSecondary, mt: '3px', flexShrink: 0 }} />}
            <Typography variant="body2" sx={{ color: c.textSecondary, fontSize: '0.8rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
              {text}
            </Typography>
          </Box>
        ))}

        {view.details.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(0, 1fr)',
              columnGap: 1.25,
              rowGap: 0.25,
              mt: 0.75
            }}
          >
            {view.details.map(({ label, value, mono }) => (
              <Fragment key={label}>
                <Typography variant="caption" sx={{ color: c.textSecondary, fontWeight: 600, lineHeight: 1.55 }}>
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: c.text,
                    lineHeight: 1.55,
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                    ...(mono && { fontFamily: MONO_FONT, fontSize: '0.78rem', letterSpacing: '0.02em' })
                  }}
                >
                  {value}
                </Typography>
              </Fragment>
            ))}
          </Box>
        )}

        {view.footer.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {view.footer.map(renderTag)}
          </Box>
        )}
      </Box>
    );
  };

  const renderSection = ({ key, label, icon, addLabel }, { total, visible }) => {
    if (query && visible.length === 0) return null;
    const actions = sectionActions[key];

    return (
      <Box key={key} ref={(el) => { sectionRefs.current[key] = el; }} sx={{ scrollMarginTop: 8 }}>
        {renderSectionHeader({
          key,
          label,
          icon,
          count: query ? `${visible.length}/${total}` : total,
          active: total > 0,
          action: (
            <Tooltip title={addLabel}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={actions.add}
                sx={{ ...tonalButtonSx, ...highlightSx(key) }}
              >
                <Box component="span" className="tonal-label">Ajouter</Box>
              </Button>
            </Tooltip>
          )
        })}
        <Collapse in={Boolean(query) || sectionExpanded[key]} timeout="auto">
          {total === 0 ? (
            <ButtonBase
              onClick={actions.add}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                border: `1px dashed ${isDark ? '#3a5163' : '#c5d0dc'}`,
                color: c.textSecondary,
                fontSize: '0.8rem',
                fontFamily: 'inherit',
                transition: 'all 0.15s ease',
                '&:hover, &.Mui-focusVisible': {
                  borderColor: c.accent,
                  color: c.accent,
                  backgroundColor: c.accentSoft
                }
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              {addLabel}
            </ButtonBase>
          ) : (
            <Paper elevation={0} sx={cardSx}>
              {visible.map(({ item, view }, index) => (
                <Fragment key={item._id || index}>
                  {index > 0 && <Divider sx={{ borderColor: c.border }} />}
                  {renderItemRow(key, item, view)}
                </Fragment>
              ))}
            </Paper>
          )}
        </Collapse>
      </Box>
    );
  };

  const infoLabelSx = {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: c.textSecondary,
    lineHeight: 1.6
  };

  const infoValueSx = (defined) => (defined
    ? { color: c.text, fontWeight: 500, lineHeight: 1.45 }
    : { color: c.textSecondary, fontStyle: 'italic', lineHeight: 1.45 });

  const renderInfoRow = ({ icon: Icon, label, defined, onEdit, editTitle, extraActions = [], children }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 1.25, py: 1.1 }}>
      <Box sx={{ ...iconBadgeSx(defined, 30), mt: 0.25 }}>
        <Icon sx={{ fontSize: 17 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={infoLabelSx}>{label}</Typography>
        {children}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
        {extraActions.map(renderAction)}
        <Tooltip title={editTitle}>
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={onEdit}
            sx={{ ...tonalButtonSx, ...highlightSx() }}
          >
            <Box component="span" className="tonal-label">Modifier</Box>
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderGeneralSection = () => {
    const structure = reportData?.reportType;
    const structureLabel = structure === 'initial'
      ? 'Procès-verbal initial'
      : structure === 'subsequent' ? 'Procès-verbal subséquent' : 'Non défini';
    const startDefined = Boolean(reportData?.StartDate);
    const endDefined = Boolean(reportData?.EndDate);
    const savedUnit = reportData?.unit;

    return (
      <Box ref={(el) => { sectionRefs.current.general = el; }}>
        {renderSectionHeader({ key: 'general', label: 'Informations générales', icon: TuneIcon, active: true })}
        <Collapse in={sectionExpanded.general} timeout="auto">
          <Paper elevation={0} sx={cardSx}>
            {renderInfoRow({
              icon: AccountTreeOutlinedIcon,
              label: 'Structure',
              defined: Boolean(structure),
              onEdit: handleOpenTypeDialog,
              editTitle: 'Modifier le type de procès-verbal',
              children: (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                  <Typography variant="body2" sx={infoValueSx(Boolean(structure))}>
                    {structureLabel}
                  </Typography>
                  {structure === 'initial' && renderTag(reportData?.indice
                    ? { label: `Indice ${reportData.indice}`, tone: 'accent' }
                    : { label: 'Indice non défini', tone: 'warning' })}
                </Box>
              )
            })}
            <Divider sx={{ borderColor: c.border }} />
            {renderInfoRow({
              icon: EventNoteOutlinedIcon,
              label: 'Rédaction',
              defined: startDefined || endDefined,
              onEdit: handleOpenRedactionDialog,
              editTitle: 'Modifier les informations de rédaction',
              children: (
                <>
                  <Typography variant="body2" sx={infoValueSx(startDefined)}>
                    {startDefined ? `Débuté le ${formatStartDate(reportData.StartDate)}` : 'Début non défini'}
                  </Typography>
                  <Typography variant="body2" sx={{ ...infoValueSx(endDefined), mt: 0.25 }}>
                    {endDefined ? `Clos le ${formatEndDate(reportData.EndDate)}` : 'Clôture non définie'}
                  </Typography>
                  <Tooltip title="Définir la date de clôture à aujourd'hui">
                    <Button
                      size="small"
                      startIcon={<EventAvailableIcon />}
                      onClick={handleSetEndDateToday}
                      sx={{
                        mt: 0.75,
                        minHeight: 26,
                        py: 0.25,
                        px: 1,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        color: c.accent,
                        border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.35)' : 'rgba(25, 118, 210, 0.3)'}`,
                        '& .MuiButton-startIcon': { mr: 0.5 },
                        '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 16 },
                        '&:hover': { backgroundColor: c.accentSoft }
                      }}
                    >
                      Clôturer aujourd'hui
                    </Button>
                  </Tooltip>
                </>
              )
            })}
            <Divider sx={{ borderColor: c.border }} />
            {renderInfoRow({
              icon: LocalPoliceOutlinedIcon,
              label: 'Service',
              defined: Boolean(savedUnit),
              onEdit: handleOpenUnitDialog,
              editTitle: 'Modifier le service',
              extraActions: [{
                title: 'Copier dans le presse-papier',
                icon: ContentCopyIcon,
                onClick: () => handleCopyToClipboard(savedUnit),
                disabled: !savedUnit
              }],
              children: (
                <Typography variant="body2" sx={{ ...infoValueSx(Boolean(savedUnit)), wordBreak: 'break-word' }}>
                  {savedUnit || 'Non défini'}
                </Typography>
              )
            })}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  const pvLabel = formatReportNumber(report?.number, report?.year);

  const dialogPaperProps = {
    sx: {
      borderRadius: '12px',
      backgroundColor: c.surface,
      backgroundImage: 'none',
      color: c.text
    }
  };

  const renderDialogTitle = ({ icon: Icon, title, subtitle = pvLabel && `PV ${pvLabel}`, onClose, tone = 'accent' }) => (
    <DialogTitle
      component="div"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${c.border}`
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          backgroundColor: tone === 'danger' ? c.dangerSoft : c.accentSoft,
          color: tone === 'danger' ? c.danger : c.accent
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: c.text, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: c.textSecondary }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton onClick={onClose} size="small" aria-label="Fermer" sx={{ color: c.textSecondary }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );

  const dialogContentSx = {
    px: 2.5,
    pb: 2,
    '& .MuiInputBase-root': {
      backgroundColor: isDark ? '#1a252f' : '#fff',
      color: c.text
    },
    '& .MuiOutlinedInput-root:not(.Mui-error)': {
      '& fieldset': { borderColor: isDark ? c.border : undefined },
      '&:hover fieldset': { borderColor: isDark ? c.accent : undefined },
      '&.Mui-focused fieldset': { borderColor: c.accent }
    },
    '& .MuiInputLabel-root:not(.Mui-error)': {
      color: c.label,
      '&.Mui-focused': { color: c.accent }
    },
    '& .MuiFormHelperText-root:not(.Mui-error)': { color: c.textSecondary },
    '& .MuiFormControlLabel-label': { fontSize: '0.9rem', color: c.text },
    '& .MuiCheckbox-root, & .MuiRadio-root': { color: c.textSecondary },
    '& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked': { color: c.accent },
    '& .MuiCheckbox-root.Mui-disabled': { color: c.textSecondary, opacity: 0.5 },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.textSecondary }
  };

  const toggleGroupSx = {
    '& .MuiToggleButton-root': {
      py: 0.75,
      gap: 0.75,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.85rem',
      color: c.textSecondary,
      borderColor: c.border,
      '&.Mui-selected': {
        color: c.accent,
        backgroundColor: c.accentSoft,
        '&:hover': { backgroundColor: c.accentSoftHover }
      }
    }
  };

  const groupTitleSx = {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: c.textSecondary,
    mb: 0.25
  };

  const fullRowSx = { gridColumn: '1 / -1' };

  const renderFieldGroup = (title, children, { columns = 1, first = false } = {}) => (
    <Box sx={{ mt: first ? 0 : 2.5 }}>
      {title && <Typography sx={groupTitleSx}>{title}</Typography>}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: columns > 1 ? { xs: '1fr', sm: `repeat(${columns}, minmax(0, 1fr))` } : '1fr',
          columnGap: 1.5
        }}
      >
        {children}
      </Box>
    </Box>
  );

  const primaryButtonSx = isDark
    ? { backgroundColor: c.accent, color: '#000', '&:hover': { backgroundColor: c.accentHover } }
    : undefined;

  const renderDialogActions = ({ onCancel, onConfirm, confirmLabel, disabled = false, danger = false, icon }) => (
    <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${c.border}` }}>
      <Button onClick={onCancel} sx={{ color: isDark ? '#bdc3c7' : 'text.secondary' }}>
        Annuler
      </Button>
      <Button
        variant="contained"
        color={danger ? 'error' : 'primary'}
        onClick={onConfirm}
        disabled={disabled}
        startIcon={icon}
        disableElevation
        sx={danger ? undefined : primaryButtonSx}
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  );

  const handleOpenRepriseDialog = () => {
    setOpenRepriseDialog(true);
  };

  const handleCloseRepriseDialog = () => {
    setOpenRepriseDialog(false);
  };

  // Gestion du dialog de modification du type de PV
  const handleOpenTypeDialog = () => {
    setOpenTypeDialog(true);
    setReportType(reportData?.reportType || 'subsequent');
    setIndice(reportData?.indice || '');
    setIndiceError('');
  };

  const handleCloseTypeDialog = () => {
    setOpenTypeDialog(false);
  };

  const validateIndice = (value) => {
    if (reportType === 'initial') {
      if (!value || value.trim() === '') {
        setIndiceError('L\'indice est obligatoire pour un PV initial');
        return false;
      }
      if (!/^\d{2}$/.test(value)) {
        setIndiceError('L\'indice doit être composé de 2 chiffres');
        return false;
      }
    }
    setIndiceError('');
    return true;
  };

  const handleSaveReportType = async () => {
    if (!validateIndice(indice)) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du document non disponible');
        return;
      }

      const updatedData = {
        reportType,
        indice: reportType === 'initial' ? indice : ''
      };

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'reportType',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        ...updatedData
      }));
      setReportType(updatedData.reportType);
      setIndice(updatedData.indice);

      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          ...updatedData,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/type`,
        updatedData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification de la structure PV
      logAction(LOG_ACTIONS.ELEMENTS_STRUCTURE_UPDATE, formatReportNumber(report.number, report.year), {
        ancienReportType: reportData?.reportType || null,
        ancienIndice: reportData?.indice || null,
        nouveauReportType: updatedData.reportType,
        nouveauIndice: updatedData.indice
      });
      
      handleCloseTypeDialog();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du type de PV:', error);
      handleCloseTypeDialog();
    }
  };

  if (loading && !reportData) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          p: 3,
          backgroundColor: isDark ? c.panel : 'inherit'
        }}
      >
        <CircularProgress size={28} sx={{ color: c.accent }} />
        <Typography variant="body2" sx={{ color: c.textSecondary }}>
          Chargement de l'encodage…
        </Typography>
      </Box>
    );
  }

  const sectionEntries = SECTIONS.reduce((acc, { key }) => {
    const entries = (reportData?.[key] || []).map((item) => ({ item, view: getItemView(key, item) }));
    return {
      ...acc,
      [key]: { total: entries.length, visible: query ? entries.filter(({ view }) => matchesFilter(view)) : entries }
    };
  }, {});
  const noFilterMatch = Boolean(query) && SECTIONS.every(({ key }) => sectionEntries[key].visible.length === 0);
  const allExpanded = COLLAPSIBLE_KEYS.every((key) => sectionExpanded[key]);

  // Aperçu de l'élément à supprimer, conservé pendant la fermeture du dialogue
  const deleteSection = SECTIONS.find((section) => section.key === deleteType);
  const deleteItem = deleteSection && reportData?.[deleteType]?.find((item) => item._id === itemToDelete);
  if (deleteItem) {
    lastDeletePreviewRef.current = { section: deleteSection, view: getItemView(deleteType, deleteItem) };
  }
  const deletePreview = lastDeletePreviewRef.current;
  const DeletePreviewIcon = deletePreview?.section.icon;

  const bottomActions = [
    {
      label: 'Reprendre',
      icon: LoupeIcon,
      onClick: handleOpenRepriseDialog,
      tooltip: 'Reprendre des éléments et des rubriques depuis un autre PV'
    },
    {
      label: 'Importer depuis ISLP',
      icon: FileUploadIcon,
      onClick: handleOpenImportISLP,
      tooltip: 'Importer des données copiées depuis ISLP'
    }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        px: 1,
        pt: 0.5,
        backgroundColor: isDark ? c.panel : 'inherit'
      }}
    >
      <Box sx={{ containerType: 'inline-size', containerName: 'encodage' }}>
        {renderGeneralSection()}

        {/* Filtre et repli des sections */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, mb: 0.25 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Filtrer : nom, plaque, fait…"
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setFilterText('');
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: filterText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Effacer le filtre"
                    onClick={() => setFilterText('')}
                    sx={{ color: c.textSecondary, mr: -0.75 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{
              '& .MuiInputBase-root': {
                height: 34,
                fontSize: '0.85rem',
                borderRadius: '8px',
                backgroundColor: c.surface,
                color: c.text
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: c.border },
                '&:hover fieldset': { borderColor: c.accent },
                '&.Mui-focused fieldset': { borderColor: c.accent, borderWidth: 1 }
              },
              '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.textSecondary }
            }}
          />
          {renderAction({
            title: allExpanded ? 'Replier toutes les sections' : 'Déplier toutes les sections',
            icon: allExpanded ? UnfoldLessIcon : UnfoldMoreIcon,
            onClick: () => setAllSectionsExpanded(!allExpanded),
            disabled: Boolean(query)
          })}
        </Box>

        {SECTIONS.map((section) => renderSection(section, sectionEntries[section.key]))}

        {noFilterMatch && (
          <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
            <SearchIcon sx={{ fontSize: 28, color: c.textSecondary, opacity: 0.6 }} />
            <Typography variant="body2" sx={{ color: c.textSecondary, mt: 0.5 }}>
              Aucun élément ne correspond à « {filterText.trim()} ».
            </Typography>
            <Button size="small" onClick={() => setFilterText('')} sx={{ mt: 1, color: c.accent }}>
              Effacer le filtre
            </Button>
          </Box>
        )}
      </Box>

      {/* Boutons Reprendre et Importer depuis ISLP */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 3,
          mt: 'auto',
          pt: 2,
          pb: 1,
          background: `linear-gradient(to bottom, ${c.panel}00 0%, ${c.panel} 45%)`
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {bottomActions.map(({ label, icon: Icon, onClick, tooltip }) => (
            <Tooltip key={label} title={tooltip}>
              <Button
                variant="text"
                startIcon={<Icon />}
                onClick={onClick}
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  borderRadius: '12px',
                  px: 1.5,
                  py: 1,
                  fontWeight: 600,
                  lineHeight: 1.25,
                  color: isDark ? '#4fc3f7' : '#1565c0',
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(21, 101, 192, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.4)' : 'rgba(21, 101, 192, 0.3)'}`,
                  backdropFilter: 'saturate(160%) blur(8px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(0,0,0,0.25)'
                    : '0 3px 10px rgba(21, 101, 192, 0.15)',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.25)' : 'rgba(21, 101, 192, 0.15)',
                    boxShadow: isDark
                      ? '0 6px 16px rgba(0,0,0,0.35)'
                      : '0 4px 14px rgba(21, 101, 192, 0.2)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {label}
              </Button>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Dialog de modification d'unité */}
      <Dialog open={openUnitDialog} onClose={handleCloseUnitDialog} maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({ icon: LocalPoliceOutlinedIcon, title: 'Modifier le service', onClose: handleCloseUnitDialog })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 1.5 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Unité"
              type="text"
              fullWidth
              variant="outlined"
              value={unit}
              onChange={(e) => {
                setUnit(e.target.value);
                validateUnit(e.target.value);
              }}
              error={!!unitError}
              helperText={unitError || 'Service chargé de la rédaction du procès-verbal'}
              size="small"
            />
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseUnitDialog,
          onConfirm: handleSaveUnit,
          confirmLabel: 'Modifier',
          disabled: !unit || !!unitError
        })}
      </Dialog>

      {/* Dialog pour ajouter/modifier un intervenant */}
      <Dialog open={openIntervenantDialog} onClose={handleCloseIntervenantDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: BadgeIcon,
          title: intervenantMode === 'add' ? 'Ajouter un intervenant' : 'Modifier l\'intervenant',
          onClose: handleCloseIntervenantDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 2 }}>
            <ToggleButtonGroup
              value={isExternalMember ? 'external' : 'listed'}
              exclusive
              onChange={(_, newValue) => {
                if (!newValue) return;
                const external = newValue === 'external';
                setIsExternalMember(external);
                setIntervenantErrors({});
                if (!external) {
                  syncFlagsFromUser(selectedUserId, { opj: false, opa: false });
                }
              }}
              fullWidth
              size="small"
              sx={toggleGroupSx}
            >
              <ToggleButton value="listed">
                <PersonIcon sx={{ fontSize: 18 }} />
                Membre listé
              </ToggleButton>
              <ToggleButton value="external">
                <BadgeIcon sx={{ fontSize: 18 }} />
                Membre extérieur
              </ToggleButton>
            </ToggleButtonGroup>

            {renderFieldGroup('Identité', isExternalMember ? (
              <>
                <FormControl fullWidth margin="dense" size="small" error={!!intervenantErrors.rank} sx={fullRowSx}>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    value={rank}
                    label="Grade"
                    onChange={(e) => setRank(e.target.value)}
                  >
                    {gradesList.map((grade) => (
                      <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                    ))}
                  </Select>
                  {intervenantErrors.rank && <FormHelperText>{intervenantErrors.rank}</FormHelperText>}
                </FormControl>
                <TextField
                  margin="dense"
                  label="Nom"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  error={!!intervenantErrors.lastname}
                  helperText={intervenantErrors.lastname}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Prénom"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  error={!!intervenantErrors.firstname}
                  helperText={intervenantErrors.firstname}
                  size="small"
                />
              </>
            ) : (
              <Autocomplete
                options={users}
                value={users.find((u) => u._id === selectedUserId) || null}
                onChange={(_, selected) => {
                  const value = selected?._id || '';
                  setSelectedUserId(value);
                  syncFlagsFromUser(value, { opj: false, opa: false });
                }}
                getOptionLabel={(option) => `${option.lastname?.toUpperCase() || ''} ${option.firstname || ''} (${option.rank || 'Membre'})`}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={option._id} {...optionProps}>
                      {`${option.lastname?.toUpperCase() || ''} ${option.firstname || ''} (${option.rank || 'Membre'})`}
                    </li>
                  );
                }}
                noOptionsText="Aucun utilisateur ne correspond"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Intervenant"
                    placeholder="Rechercher par nom…"
                    margin="dense"
                    size="small"
                    error={!!intervenantErrors.userId}
                    helperText={intervenantErrors.userId}
                  />
                )}
              />
            ))}

            {renderFieldGroup('Rôle', (
              <>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSignataire}
                        onChange={(e) => setIsSignataire(e.target.checked)}
                      />
                    }
                    label="Signataire"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isOPJ}
                        onChange={(e) => setIsOPJ(e.target.checked)}
                        disabled={!isExternalMember}
                      />
                    }
                    label="OPJ"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isOPA}
                        onChange={(e) => setIsOPA(e.target.checked)}
                        disabled={!isExternalMember}
                      />
                    }
                    label="OPA"
                  />
                </FormGroup>
                {!isExternalMember && (
                  <FormHelperText sx={{ mt: 0 }}>
                    Les statuts OPJ/OPA sont déterminés automatiquement selon l'utilisateur choisi.
                  </FormHelperText>
                )}
              </>
            ))}
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseIntervenantDialog,
          onConfirm: handleSaveIntervenant,
          confirmLabel: intervenantMode === 'add' ? 'Ajouter' : 'Modifier',
          disabled: isExternalMember ? (!rank || !lastname || !firstname) : !selectedUserId
        })}
      </Dialog>

      {/* Dialog pour ajouter/modifier une personne physique */}
      <Dialog open={openPersonneDialog} onClose={handleClosePersonneDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: PersonIcon,
          title: personneMode === 'add' ? 'Ajouter une personne physique' : 'Modifier la personne physique',
          onClose: handleClosePersonneDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 2 }}>
            {renderFieldGroup('Qualité', (
              <>
                <Autocomplete
                  freeSolo
                  options={personneQualitesList}
                  value={personneQualite}
                  onChange={(event, newValue) => {
                    if (newValue && !personneQualitesList.includes(newValue)) {
                      setPersonneQualitesList(prev => [...prev, newValue]);
                    }
                    setPersonneQualite(newValue || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    setPersonneQualite(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Qualité"
                      margin="dense"
                      size="small"
                      error={!!personneErrors.qualite}
                      helperText={personneErrors.qualite}
                      fullWidth
                    />
                  )}
                />

                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Qualités additionnelles</InputLabel>
                  <Select
                    multiple
                    value={additionalQualities}
                    label="Qualités additionnelles"
                    onChange={(e) => setAdditionalQualities(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    <MenuItem value="absent">
                      <Checkbox checked={additionalQualities.indexOf('absent') > -1} />
                      <ListItemText primary="absent" />
                    </MenuItem>
                    <MenuItem value="blessé léger">
                      <Checkbox checked={additionalQualities.indexOf('blessé léger') > -1} />
                      <ListItemText primary="blessé léger" />
                    </MenuItem>
                    <MenuItem value="blessé grave">
                      <Checkbox checked={additionalQualities.indexOf('blessé grave') > -1} />
                      <ListItemText primary="blessé grave" />
                    </MenuItem>
                    <MenuItem value="décédé">
                      <Checkbox checked={additionalQualities.indexOf('décédé') > -1} />
                      <ListItemText primary="décédé" />
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isUnknown}
                      onChange={(e) => setIsUnknown(e.target.checked)}
                    />
                  }
                  label="Individu inconnu"
                  sx={{ ...fullRowSx, mt: 0.5 }}
                />
              </>
            ), { columns: 2, first: true })}

            {!isUnknown && (
              <>
                {renderFieldGroup('Identité', (
                  <>
                    <TextField
                      margin="dense"
                      label="Nom"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={personneLastname}
                      onChange={(e) => setPersonneLastname(e.target.value)}
                      error={!!personneErrors.lastname}
                      helperText={personneErrors.lastname}
                      size="small"
                    />
                    <TextField
                      margin="dense"
                      label="Prénom"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={personneFirstname}
                      onChange={(e) => setPersonneFirstname(e.target.value)}
                      size="small"
                    />
                    <TextField
                      margin="dense"
                      label="Autres prénoms"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={otherFirstnames}
                      onChange={(e) => setOtherFirstnames(e.target.value)}
                      size="small"
                      sx={fullRowSx}
                    />
                    <FormControl component="fieldset" sx={{ ...fullRowSx, mt: 1 }}>
                      <Typography variant="body2" sx={{ color: c.textSecondary }}>
                        Sexe
                      </Typography>
                      <RadioGroup
                        row
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                      >
                        <FormControlLabel value="M" control={<Radio />} label="Masculin" />
                        <FormControlLabel value="F" control={<Radio />} label="Féminin" />
                      </RadioGroup>
                    </FormControl>
                  </>
                ), { columns: 2 })}

                {renderFieldGroup('Naissance', (
                  <>
                    <TextField
                      margin="dense"
                      label="Lieu de naissance"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={birthPlace}
                      onChange={(e) => setBirthPlace(e.target.value)}
                      size="small"
                    />
                    <TextField
                      margin="dense"
                      label="Date de naissance"
                      type="date"
                      fullWidth
                      variant="outlined"
                      value={birthdate}
                      onChange={(e) => setBirthdate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                    />
                  </>
                ), { columns: 2 })}

                {renderFieldGroup('Situation', (
                  <>
                    <TextField
                      margin="dense"
                      label="État civil"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={civilStatus}
                      onChange={(e) => setCivilStatus(e.target.value)}
                      size="small"
                    />
                    <TextField
                      margin="dense"
                      label="Nationalité"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                      size="small"
                    />
                    <TextField
                      margin="dense"
                      label="Profession"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      size="small"
                      sx={fullRowSx}
                    />
                    <TextField
                      margin="dense"
                      label="Domicile"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={residence}
                      onChange={(e) => setResidence(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      sx={fullRowSx}
                    />
                  </>
                ), { columns: 2 })}

                {renderFieldGroup('Identifiants', (
                  <>
                    <TextField
                      margin="dense"
                      label="Numéro national"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={nationalNumber}
                      onChange={(e) => setNationalNumber(e.target.value)}
                      size="small"
                    />
                    <TextField
                      margin="dense"
                      label="Numéro APFIS"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={raavisNumber}
                      onChange={(e) => setRaavisNumber((e.target.value || '').toUpperCase())}
                      size="small"
                    />
                  </>
                ), { columns: 2 })}

                {renderFieldGroup('Triptyque', (
                  <>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Photographies</InputLabel>
                      <Select
                        value={photographies}
                        label="Photographies"
                        onChange={(e) => setPhotographies(e.target.value)}
                      >
                        <MenuItem value="">Ne pas mentionner</MenuItem>
                        <MenuItem value="Oui">Oui</MenuItem>
                        <MenuItem value="Non">Non</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Empreintes</InputLabel>
                      <Select
                        value={empreintes}
                        label="Empreintes"
                        onChange={(e) => setEmpreintes(e.target.value)}
                      >
                        <MenuItem value="">Ne pas mentionner</MenuItem>
                        <MenuItem value="Oui">Oui</MenuItem>
                        <MenuItem value="Non">Non</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Notice individuelle</InputLabel>
                      <Select
                        value={noticeIndividuelle}
                        label="Notice individuelle"
                        onChange={(e) => setNoticeIndividuelle(e.target.value)}
                      >
                        <MenuItem value="">Ne pas mentionner</MenuItem>
                        <MenuItem value="Oui">Oui</MenuItem>
                        <MenuItem value="Non">Non</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                ), { columns: 3 })}
              </>
            )}
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleClosePersonneDialog,
          onConfirm: handleSavePersonne,
          confirmLabel: personneMode === 'add' ? 'Ajouter' : 'Modifier',
          disabled: !personneQualite || (!isUnknown && !personneLastname)
        })}
      </Dialog>

      {/* Dialog pour ajouter/modifier un objet */}
      <Dialog open={openObjetDialog} onClose={handleCloseObjetDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: Inventory2Icon,
          title: objetMode === 'add' ? 'Ajouter un objet' : 'Modifier l\'objet',
          onClose: handleCloseObjetDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 1.5 }}>
            <TextField
              margin="dense"
              label="Description et nombre"
              type="text"
              fullWidth
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={!!objetErrors.description}
              helperText={objetErrors.description}
              size="small"
            />

            <TextField
              margin="dense"
              label="Numéro SIN"
              type="text"
              fullWidth
              variant="outlined"
              value={sinNumber}
              onChange={(e) => setSinNumber(e.target.value)}
              helperText="Facultatif"
              size="small"
            />
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseObjetDialog,
          onConfirm: handleSaveObjet,
          confirmLabel: objetMode === 'add' ? 'Ajouter' : 'Modifier',
          disabled: !description
        })}
      </Dialog>

      {/* Dialog pour ajouter/modifier un véhicule */}
      <Dialog open={openVehiculeDialog} onClose={handleCloseVehiculeDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: DirectionsCarIcon,
          title: vehiculeMode === 'add' ? 'Ajouter un véhicule' : 'Modifier le véhicule',
          onClose: handleCloseVehiculeDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 2 }}>
            {renderFieldGroup('Véhicule', (
              <>
                <TextField
                  margin="dense"
                  label="Marque et modèle"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  error={!!vehiculeErrors.brand}
                  helperText={vehiculeErrors.brand}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Catégorie"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={vehiculeCategory}
                  onChange={(e) => setVehiculeCategory(e.target.value)}
                  size="small"
                />
              </>
            ), { columns: 2, first: true })}

            {renderFieldGroup('Identification', (
              <>
                <TextField
                  margin="dense"
                  label="Immatriculation"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={plateNumber}
                  onChange={(e) => setPlateNumber((e.target.value || '').toUpperCase())}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Numéro de châssis/cadre"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={chassisNumber}
                  onChange={(e) => setChassisNumber((e.target.value || '').toUpperCase())}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Nationalité"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={vehiculeNationality}
                  onChange={(e) => setVehiculeNationality(e.target.value)}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Teinte"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={vehiculeColor}
                  onChange={(e) => setVehiculeColor(e.target.value)}
                  helperText="Facultatif"
                  size="small"
                />
              </>
            ), { columns: 2 })}

            {renderFieldGroup('Documents', (
              <>
                <TextField
                  margin="dense"
                  label="Assurance"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={insurance}
                  onChange={(e) => setInsurance(e.target.value)}
                  helperText="Facultatif"
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Contrôle technique"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={technicalControl}
                  onChange={(e) => setTechnicalControl(e.target.value)}
                  helperText="Facultatif"
                  size="small"
                />
              </>
            ), { columns: 2 })}
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseVehiculeDialog,
          onConfirm: handleSaveVehicule,
          confirmLabel: vehiculeMode === 'add' ? 'Ajouter' : 'Modifier',
          disabled: !brand
        })}
      </Dialog>

      {/* Dialog pour ajouter/modifier une personne morale */}
      <Dialog open={openPersonneMoraleDialog} onClose={handleClosePersonneMoraleDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: BusinessIcon,
          title: personneMoraleMode === 'add' ? 'Ajouter une personne morale' : 'Modifier la personne morale',
          onClose: handleClosePersonneMoraleDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 1.5 }}>
            {renderFieldGroup(null, (
              <>
                <Autocomplete
                  freeSolo
                  options={personneMoraleQualitesList}
                  value={personneMoraleQualite}
                  onChange={(event, newValue) => {
                    if (newValue && !personneMoraleQualitesList.includes(newValue)) {
                      setPersonneMoraleQualitesList(prev => [...prev, newValue]);
                    }
                    setPersonneMoraleQualite(newValue || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    setPersonneMoraleQualite(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Qualité"
                      margin="dense"
                      size="small"
                      error={!!personneMoraleErrors.qualite}
                      helperText={personneMoraleErrors.qualite}
                      fullWidth
                    />
                  )}
                  sx={fullRowSx}
                />
                <TextField
                  margin="dense"
                  label="Dénomination"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={denomination}
                  onChange={(e) => setDenomination(e.target.value)}
                  error={!!personneMoraleErrors.denomination}
                  helperText={personneMoraleErrors.denomination}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Numéro d'entreprise"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Adresse du siège social"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={headquarters}
                  onChange={(e) => setHeadquarters(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                  sx={fullRowSx}
                />
              </>
            ), { columns: 2, first: true })}
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleClosePersonneMoraleDialog,
          onConfirm: handleSavePersonneMorale,
          confirmLabel: personneMoraleMode === 'add' ? 'Ajouter' : 'Modifier',
          disabled: !personneMoraleQualite || !denomination
        })}
      </Dialog>

      {/* Dialog pour ajouter/modifier un fait */}
      <Dialog open={openFaitDialog} onClose={handleCloseFaitDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: GavelIcon,
          title: faitMode === 'add' ? 'Ajouter un fait' : 'Modifier le fait',
          onClose: handleCloseFaitDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUndetermined}
                  onChange={(e) => setIsUndetermined(e.target.checked)}
                />
              }
              label="Fait non déterminé - PV subséquent"
            />

            {!isUndetermined && (
              <>
                {renderFieldGroup('Qualification', (
                  <>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isQualified}
                            onChange={(e) => setIsQualified(e.target.checked)}
                          />
                        }
                        label="Fait qualifié (mineur)"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isTentative}
                            onChange={(e) => setIsTentative(e.target.checked)}
                          />
                        }
                        label="Tentative"
                      />
                    </FormGroup>
                    <TextField
                      margin="dense"
                      label="Fait"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={fait}
                      onChange={(e) => setFait(e.target.value)}
                      error={!!faitErrors.fait}
                      helperText={faitErrors.fait}
                      size="small"
                      multiline
                      rows={2}
                    />
                    <TextField
                      margin="dense"
                      label="Article"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={article}
                      onChange={(e) => setArticle(e.target.value)}
                      helperText="Facultatif"
                      size="small"
                    />
                  </>
                ))}

                {renderFieldGroup('Moment', (
                  <>
                    <ToggleButtonGroup
                      value={timeType}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue) setTimeType(newValue);
                      }}
                      fullWidth
                      size="small"
                      sx={{ ...toggleGroupSx, ...fullRowSx, mt: 0.5, mb: 0.5 }}
                    >
                      <ToggleButton value="fixed">
                        <ScheduleIcon sx={{ fontSize: 18 }} />
                        Heure fixe
                      </ToggleButton>
                      <ToggleButton value="range">
                        <DateRangeIcon sx={{ fontSize: 18 }} />
                        Durée
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {timeType === 'fixed' ? (
                      <TextField
                        margin="dense"
                        label="Date et heure"
                        type="datetime-local"
                        fullWidth
                        variant="outlined"
                        value={fixedDateTime}
                        onChange={(e) => setFixedDateTime(e.target.value)}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={fullRowSx}
                      />
                    ) : (
                      <>
                        <TextField
                          margin="dense"
                          label="Date et heure basse"
                          type="datetime-local"
                          fullWidth
                          variant="outlined"
                          value={rangeLowDateTime}
                          onChange={(e) => setRangeLowDateTime(e.target.value)}
                          size="small"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                        <TextField
                          margin="dense"
                          label="Date et heure haute"
                          type="datetime-local"
                          fullWidth
                          variant="outlined"
                          value={rangeHighDateTime}
                          onChange={(e) => setRangeHighDateTime(e.target.value)}
                          size="small"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </>
                    )}
                  </>
                ), { columns: 2 })}

                {renderFieldGroup('Lieu', (
                  <>
                    <TextField
                      margin="dense"
                      label="Adresse"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      error={!!faitErrors.address}
                      helperText={faitErrors.address}
                      size="small"
                      multiline
                      rows={2}
                    />
                    <TextField
                      margin="dense"
                      label="Pays"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      size="small"
                      helperText="Sera préfixé à l'adresse"
                    />
                    <TextField
                      margin="dense"
                      label="Destination du lieu"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      helperText="Optionnel"
                    />
                  </>
                ))}
              </>
            )}
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseFaitDialog,
          onConfirm: handleSaveFait,
          confirmLabel: faitMode === 'add' ? 'Ajouter' : 'Modifier',
          disabled: !isUndetermined && (!fait || !address)
        })}
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: DeleteForeverOutlinedIcon,
          tone: 'danger',
          title: 'Supprimer cet élément ?',
          subtitle: deletePreview?.section.singular,
          onClose: handleCloseDeleteDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 2 }}>
            {deletePreview && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1.25,
                  mb: 1.5,
                  borderRadius: '10px',
                  border: `1px solid ${c.border}`,
                  backgroundColor: c.previewBg
                }}
              >
                <Box sx={iconBadgeSx(true, 32)}>
                  <DeletePreviewIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: c.text, wordBreak: 'break-word' }}>
                    {deletePreview.view.primary}
                  </Typography>
                  {(deletePreview.view.lines[0] || deletePreview.view.tags[0]) && (
                    <Typography variant="caption" sx={{ color: c.textSecondary, display: 'block', wordBreak: 'break-word' }}>
                      {deletePreview.view.lines[0]?.text || deletePreview.view.tags[0]?.label}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            <Typography variant="body2" sx={{ color: c.textSecondary }}>
              Cette action est irréversible.
            </Typography>
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseDeleteDialog,
          onConfirm: handleDeleteItem,
          confirmLabel: 'Supprimer',
          danger: true,
          icon: <DeleteOutlineIcon />
        })}
      </Dialog>

      {/* Dialog de modification de rédaction */}
      <Dialog open={openRedactionDialog} onClose={handleCloseRedactionDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: EventNoteOutlinedIcon,
          title: 'Modifier les informations de rédaction',
          onClose: handleCloseRedactionDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 1.5 }}>
            {renderFieldGroup(null, (
              <>
                <TextField
                  margin="dense"
                  label="Date et heure de début"
                  type="datetime-local"
                  fullWidth
                  variant="outlined"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  error={!!redactionErrors.startDate}
                  helperText={redactionErrors.startDate}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  margin="dense"
                  label="Date de clôture"
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  error={!!redactionErrors.endDate}
                  helperText={redactionErrors.endDate}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </>
            ), { columns: 2, first: true })}
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseRedactionDialog,
          onConfirm: handleSaveRedaction,
          confirmLabel: 'Enregistrer'
        })}
      </Dialog>

      {/* Dialog de modification du type de PV */}
      <Dialog open={openTypeDialog} onClose={handleCloseTypeDialog} maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
        {renderDialogTitle({
          icon: AccountTreeOutlinedIcon,
          title: 'Modifier le type de procès-verbal',
          onClose: handleCloseTypeDialog
        })}
        <DialogContent sx={dialogContentSx}>
          <Box sx={{ pt: 2 }}>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              fullWidth
              size="small"
              onChange={(_, value) => {
                if (!value) return;
                setReportType(value);
                if (value !== 'initial') {
                  setIndiceError('');
                } else {
                  validateIndice(indice);
                }
              }}
              sx={toggleGroupSx}
            >
              <ToggleButton value="initial">PV initial</ToggleButton>
              <ToggleButton value="subsequent">PV subséquent</ToggleButton>
            </ToggleButtonGroup>

            <Collapse in={reportType === 'initial'} timeout="auto">
              <TextField
                margin="dense"
                label="Indice (2 chiffres)"
                type="text"
                fullWidth
                variant="outlined"
                value={indice}
                onChange={(e) => {
                  setIndice(e.target.value);
                  validateIndice(e.target.value);
                }}
                error={!!indiceError}
                helperText={indiceError}
                inputProps={{ maxLength: 2 }}
                size="small"
                sx={{ mt: 2 }}
              />
            </Collapse>
          </Box>
        </DialogContent>
        {renderDialogActions({
          onCancel: handleCloseTypeDialog,
          onConfirm: handleSaveReportType,
          confirmLabel: 'Modifier',
          disabled: reportType === 'initial' && (!indice || !!indiceError)
        })}
      </Dialog>

      {/* Notification de copie */}
      <Snackbar
        open={copyNotification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity="success"
          icon={<ContentCopyIcon fontSize="inherit" />}
          sx={{ width: '100%', maxWidth: 520 }}
        >
          {`« ${truncate(copiedText, 90)} » copié dans le presse-papier`}
        </Alert>
      </Snackbar>

      <ImportISLP
        open={openImportISLP}
        onClose={handleCloseImportISLP}
        report={reportData}
        reloadReportData={reloadReportData}
      />

      <RepriseDialog
        open={openRepriseDialog}
        onClose={handleCloseRepriseDialog}
        report={reportData}
        reloadReportData={reloadReportData}
      />
    </Box>
  );
}

export default Elements;
